import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Grid,
  Stack,
  Tabs,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Paper,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  IconButton,
} from '@mui/material';
import { LocalizedAlert as Alert, LocalizedButton as Button, LocalizedChip as Chip, LocalizedMenuItem as MenuItem, LocalizedTab as Tab, LocalizedTableCell as TableCell, LocalizedTextField as TextField, LocalizedTooltip as Tooltip } from '../components/i18n/LocalizedMui';
import Typography from '../components/i18n/LocalizedTypography';
import {
  AdminPanelSettings,
  Block,
  CheckCircle,
  Delete,
  Refresh,
  Visibility,
  AutoAwesome,
  Email,
  Send,
  Group,
  TableChart,
  ReceiptLong,
  PictureAsPdf,
  Download,
  NotificationsActive,
  LocationCity,
  PersonAdd,
  Campaign,
  WorkspacePremium
} from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { hasPremiumSubscription } from '../utils/subscription';
import { formatDisplayDate, formatDisplayDateTime } from '../utils/dateDisplay';

const displayChartName = (value?: string | null) => String(value || '').trim() || 'N/A';

interface AdminOverview {
  totalUsers: number;
  localUsers: number;
  activeUsers: number;
  adminUsers: number;
  localProUsers: number;
  premiumUsers: number;
  totalCharts: number;
  studioCharts: number;
  localChartsCreatedToday: number;
  chartsCreatedToday: number;
  anonymousCharts: number;
  pendingRequests: number;
}

interface AdminUser {
  _id: string;
  name: string;
  email: string;
  accountSource?: 'kundlistar' | 'studio';
  phone?: string;
  createdAt: string;
  updatedAt?: string;
  role: string;
  isActive: boolean;
  lastLogin?: string;
  loginCount?: number;
  preferences?: Record<string, boolean>;
  subscription: {
    type: string;
    chartsRemaining: number | null;
    isActive?: boolean;
    startDate?: string;
    endDate?: string;
    razorpayOrderId?: string;
    razorpaySubscriptionId?: string;
    razorpayPaymentId?: string;
    googlePlayOrderId?: string;
    googlePlayProductId?: string;
    googlePlayAutoRenewing?: boolean;
    source?: string;
    aiReportAddon?: {
      plan?: string;
      monthlyLimit?: number;
      startDate?: string;
      endDate?: string;
      isActive?: boolean;
      razorpayOrderId?: string;
      razorpaySubscriptionId?: string;
      razorpayPaymentId?: string;
      googlePlayOrderId?: string;
      googlePlayProductId?: string;
      googlePlayBasePlanId?: string;
      googlePlayAutoRenewing?: boolean;
    };
  };
  stats?: {
    chartsCreated?: number;
    loginCount?: number;
  };
  chartsCreated?: number;
  latestPayment?: {
    _id?: string;
    checkoutType?: string;
    razorpayOrderId?: string;
    razorpaySubscriptionId?: string;
    razorpayPaymentId?: string;
    amount?: number;
    currency?: string;
    subscriptionType?: string;
    status?: string;
    paymentMethod?: string;
    subscriptionStartDate?: string;
    subscriptionEndDate?: string;
    email?: string;
    phone?: string;
    customerName?: string;
    receipt?: string;
    notes?: Record<string, string>;
    paymentInitiatedAt?: string;
    paymentCompletedAt?: string;
    createdAt?: string;
  } | null;
  latestGooglePurchase?: {
    _id?: string;
    productId?: string;
    packageName?: string;
    latestOrderId?: string;
    subscriptionState?: string;
    expiryTime?: string;
    verifiedAt?: string;
    createdAt?: string;
    updatedAt?: string;
  } | null;
  aiReportsCurrentMonth?: {
    monthKey?: string;
    periodStart?: string;
    periodEnd?: string;
    generated?: number;
    quotaUsed?: number;
    limit?: number | null;
    remaining?: number | null;
    basePremiumLimit?: number;
    addonActive?: boolean;
    addonPlan?: string | null;
    reports?: Array<{
      _id?: string;
      id?: string;
      chartId?: string;
      chartName?: string;
      reportYear?: number;
      language?: string;
      generatedAt?: string;
    }>;
  };
}

interface AdminChart {
  _id: string;
  createdAt: string;
  chartType?: string;
  createdFrom?: 'web' | 'mobile' | 'admin' | 'api' | 'unknown';
  visibilityStatus?: 'shown' | 'deleted';
  birthDetails: {
    name: string;
    dateOfBirth: string;
    gender: string;
    placeOfBirth: {
      city: string;
      country: string;
    };
  };
  userId?: {
    name: string;
    email: string;
  };
}

interface PaginationMeta {
  page: number;
  limit: number;
  total: number;
  pages: number;
}

interface DemoEmailType {
  id: string;
  label: string;
  description: string;
}

interface EmailCampaignStats {
  _id: string;
  title: string;
  subject: string;
  totalEmails: number;
  sentCount?: number;
  failedCount?: number;
  pendingCount?: number;
  status: 'queued' | 'running' | 'completed' | 'paused';
  createdAt: string;
  updatedAt?: string;
  completedAt?: string | null;
  sentRowsDeletedCount?: number;
  sentRowsDeletedAt?: string | null;
  counts: {
    pending: number;
    processing: number;
    sent: number;
    failed: number;
  };
  progressPercent: number;
}

interface EmailCampaignTotals {
  totalCampaigns: number;
  totalEmails: number;
  sent: number;
  failed: number;
  pending: number;
  processing: number;
}

type NotificationTargetType = 'all' | 'anonymous' | 'registered' | 'premium' | 'non_premium' | 'selected_users';
type EmailTargetType =
  | 'registered'
  | 'premium'
  | 'non_premium'
  | 'new_premium_48h'
  | 'new_non_premium_48h'
  | 'selected_users'
  | 'manual_recipients';
type NotificationPreference = 'offers' | 'productUpdates' | 'transitAlerts' | 'dailyHoroscope' | 'muhurat';
type NotificationDeepLinkScreen =
  | 'home'
  | 'calculate'
  | 'dashboard'
  | 'nadi'
  | 'kp'
  | 'vedic'
  | 'chakra'
  | 'vastu'
  | 'jaimini'
  | 'lal-kitab'
  | 'bnn'
  | 'bnn-progression'
  | 'ruling-planets'
  | 'vedic-numerology'
  | 'astro-logo'
  | 'choghadiya'
  | 'panch-pakshi'
  | 'tarot'
  | 'daily-panchang'
  | 'daily-horoscope'
  | 'muhurat'
  | 'matching'
  | 'transit'
  | 'manual-transit'
  | 'varshphal'
  | 'horary'
  | 'explore'
  | 'subscription'
  | 'profile'
  | 'login'
  | 'register';

interface NotificationStats {
  total: number;
  active: number;
  inactive: number;
  anonymous: number;
  registered: number;
  premium: number;
  nonPremium: number;
  preferences: {
    offers: number;
    productUpdates: number;
    transitAlerts: number;
    dailyHoroscope: number;
    muhurat: number;
  };
}

interface ManualPaymentForm {
  userId: string;
  userName: string;
  subscriptionType: string;
  amount: string;
  currency: string;
  paymentMethod: string;
  razorpayOrderId: string;
  razorpaySubscriptionId: string;
  razorpayPaymentId: string;
  receipt: string;
  startDate: string;
  endDate: string;
  notes: string;
  phone: string;
}

type PremiumDurationPreset = 'monthly' | 'yearly' | 'two_days' | 'custom_days' | 'custom_dates';

interface PremiumEditForm {
  userId: string;
  userName: string;
  subscriptionType: 'free' | 'basic' | 'premium';
  durationPreset: PremiumDurationPreset;
  customDays: string;
  startDate: string;
  endDate: string;
}

interface ManualCity {
  id: string;
  name: string;
  displayName: string;
  coordinates: {
    latitude: number;
    longitude: number;
  };
  admin1?: {
    name?: string;
    code?: string;
  };
  country: {
    name: string;
    code: string;
  };
  timezone?: {
    name?: string;
    offset?: number;
  };
  population?: number;
  source?: string;
  isManual?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

interface ManualCityForm {
  name: string;
  countryCode: string;
  countryName: string;
  stateName: string;
  stateCode: string;
  latitude: string;
  longitude: string;
  timezone: string;
  population: string;
  native: string;
  translationHi: string;
}

const ADMIN_PAGE_SIZE = 50;
const ADMIN_CHARTS_LIMIT = 50;
const USER_SUBSCRIPTION_FILTERS = ['all', 'premium', 'non_premium'] as const;
const USER_ROLE_FILTERS = ['all', 'user', 'astrologer', 'admin'] as const;
const USER_STATUS_FILTERS = ['all', 'active', 'disabled'] as const;
const USER_PLAN_FILTERS = ['all', 'free', 'basic', 'premium'] as const;
const USER_PLAN_DURATION_FILTERS = ['all', 'monthly', 'yearly'] as const;
const USER_SOURCE_FILTERS = ['all', 'manual', 'revenuecat_ios'] as const;
const USER_ACCOUNT_SOURCE_FILTERS = ['all', 'kundlistar', 'studio'] as const;
const NOTIFICATION_DEEP_LINK_SCREENS: Array<{
  value: NotificationDeepLinkScreen;
  label: string;
  route: string;
}> = [
  { value: 'home', label: 'Home', route: '/(tabs)/' },
  { value: 'calculate', label: 'Calculate Kundli', route: '/(tabs)/calculate' },
  { value: 'dashboard', label: 'Saved Charts / Dashboard', route: '/(tabs)/dashboard' },
  { value: 'nadi', label: 'Nadi Analysis', route: '/(tabs)/nadi' },
  { value: 'kp', label: 'KP Analysis', route: '/(tabs)/kp' },
  { value: 'vedic', label: 'Vedic Chart', route: '/(tabs)/vedic' },
  { value: 'chakra', label: 'Chakra', route: '/(tabs)/chakra' },
  { value: 'vastu', label: 'Astro-Vastu', route: '/(tabs)/vastu' },
  { value: 'jaimini', label: 'Jaimini Analysis', route: '/(tabs)/jaimini' },
  { value: 'lal-kitab', label: 'Lal Kitab', route: '/(tabs)/lal-kitab' },
  { value: 'bnn', label: 'BNN Analysis', route: '/(tabs)/bnn' },
  { value: 'bnn-progression', label: 'BNN Progression', route: '/(tabs)/bnn-progression' },
  { value: 'ruling-planets', label: 'Ruling Planets', route: '/(tabs)/ruling-planets' },
  { value: 'vedic-numerology', label: 'Vedic Numerology', route: '/(tabs)/vedic-numerology' },
  { value: 'astro-logo', label: 'Astro Logo Designer', route: '/(tabs)/astro-logo' },
  { value: 'choghadiya', label: 'Choghadiya', route: '/(tabs)/choghadiya' },
  { value: 'panch-pakshi', label: 'Panch Pakshi', route: '/(tabs)/panch-pakshi' },
  { value: 'tarot', label: 'Vedic Tarot', route: '/(tabs)/tarot' },
  { value: 'daily-panchang', label: 'Daily Panchang', route: '/(tabs)/daily-panchang' },
  { value: 'daily-horoscope', label: 'Daily Horoscope', route: '/(tabs)/daily-horoscope' },
  { value: 'muhurat', label: 'Muhurat', route: '/(tabs)/muhurat' },
  { value: 'matching', label: 'Kundli Matching', route: '/(tabs)/matching' },
  { value: 'transit', label: 'Current Transit', route: '/(tabs)/transit' },
  { value: 'manual-transit', label: 'Manual Transit Calculator', route: '/manual-transit' },
  { value: 'varshphal', label: 'Tajika Varshphal', route: '/(tabs)/varshphal' },
  { value: 'horary', label: 'Horary Chart', route: '/(tabs)/horary' },
  { value: 'explore', label: 'Explore', route: '/(tabs)/explore' },
  { value: 'subscription', label: 'Pro / Upgrade', route: '/(tabs)/subscription' },
  { value: 'profile', label: 'My Profile', route: '/(tabs)/profile' },
  { value: 'login', label: 'Login', route: '/(auth)/login' },
  { value: 'register', label: 'Register', route: '/(auth)/register' }
];
const emptyPagination: PaginationMeta = {
  page: 1,
  limit: ADMIN_PAGE_SIZE,
  total: 0,
  pages: 0
};

type UserSubscriptionFilter = typeof USER_SUBSCRIPTION_FILTERS[number];
type UserRoleFilter = typeof USER_ROLE_FILTERS[number];
type UserStatusFilter = typeof USER_STATUS_FILTERS[number];
type UserPlanFilter = typeof USER_PLAN_FILTERS[number];
type UserPlanDurationFilter = typeof USER_PLAN_DURATION_FILTERS[number];
type UserSourceFilter = typeof USER_SOURCE_FILTERS[number];
type UserAccountSourceFilter = typeof USER_ACCOUNT_SOURCE_FILTERS[number];

type UserFilterState = {
  subscriptionStatus: UserSubscriptionFilter;
  role: UserRoleFilter;
  status: UserStatusFilter;
  plan: UserPlanFilter;
  planDuration: UserPlanDurationFilter;
  source: UserSourceFilter;
  accountSource: UserAccountSourceFilter;
};

type ExportFilter = 'current_filters' | 'all_users' | 'premium' | 'non_premium' | 'selected';

const getUrlNumber = (params: URLSearchParams, key: string, fallback: number) => {
  const parsed = Number(params.get(key));
  return Number.isFinite(parsed) && parsed > 0 ? Math.floor(parsed) : fallback;
};

const getUrlOption = <T extends readonly string[]>(
  params: URLSearchParams,
  key: string,
  options: T,
  fallback: T[number]
): T[number] => {
  const value = params.get(key);
  return value && options.includes(value) ? value : fallback;
};

const formatDateForInput = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getDefaultManualPaymentForm = (): ManualPaymentForm => {
  const start = new Date();
  const end = new Date(start);
  end.setFullYear(end.getFullYear() + 1);

  return {
    userId: '',
    userName: '',
    subscriptionType: 'premium',
    amount: '499',
    currency: 'INR',
    paymentMethod: 'upi',
    razorpayOrderId: '',
    razorpaySubscriptionId: '',
    razorpayPaymentId: '',
    receipt: '',
    startDate: formatDateForInput(start),
    endDate: formatDateForInput(end),
    notes: '',
    phone: ''
  };
};

const getDefaultPremiumEditForm = (): PremiumEditForm => {
  const start = new Date();
  const end = new Date(start);
  end.setFullYear(end.getFullYear() + 1);

  return {
    userId: '',
    userName: '',
    subscriptionType: 'premium',
    durationPreset: 'yearly',
    customDays: '365',
    startDate: formatDateForInput(start),
    endDate: formatDateForInput(end)
  };
};

const getDefaultManualCityForm = (): ManualCityForm => ({
  name: '',
  countryCode: 'IN',
  countryName: 'India',
  stateName: '',
  stateCode: '',
  latitude: '',
  longitude: '',
  timezone: 'Asia/Kolkata',
  population: '0',
  native: '',
  translationHi: ''
});

const AdminPanel: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { state } = useAuth();
  const initialUsersPage = getUrlNumber(searchParams, 'usersPage', 1);
  const initialUsersSearch = searchParams.get('usersSearch')?.trim() || '';
  const initialUserFilters: UserFilterState = {
    subscriptionStatus: getUrlOption(searchParams, 'usersSubscription', USER_SUBSCRIPTION_FILTERS, 'all'),
    role: getUrlOption(searchParams, 'usersRole', USER_ROLE_FILTERS, 'all'),
    status: getUrlOption(searchParams, 'usersStatus', USER_STATUS_FILTERS, 'all'),
    plan: getUrlOption(searchParams, 'usersPlan', USER_PLAN_FILTERS, 'all'),
    planDuration: getUrlOption(searchParams, 'usersPlanDuration', USER_PLAN_DURATION_FILTERS, 'all'),
    source: getUrlOption(searchParams, 'usersSource', USER_SOURCE_FILTERS, 'all'),
    accountSource: getUrlOption(searchParams, 'usersAccountSource', USER_ACCOUNT_SOURCE_FILTERS, 'studio')
  };

  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [overview, setOverview] = useState<AdminOverview | null>(null);
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [charts, setCharts] = useState<AdminChart[]>([]);
  const [usersPagination, setUsersPagination] = useState<PaginationMeta>({ ...emptyPagination, page: initialUsersPage });
  const [chartsPagination, setChartsPagination] = useState<PaginationMeta>(emptyPagination);
  const [chartsLoaded, setChartsLoaded] = useState(false);
  const [usersSearchInput, setUsersSearchInput] = useState(initialUsersSearch);
  const [chartsSearchInput, setChartsSearchInput] = useState('');
  const [usersSearch, setUsersSearch] = useState(initialUsersSearch);
  const [chartsSearch, setChartsSearch] = useState('');
  const [userSubscriptionFilter, setUserSubscriptionFilter] = useState<UserSubscriptionFilter>(initialUserFilters.subscriptionStatus);
  const [userRoleFilter, setUserRoleFilter] = useState<UserRoleFilter>(initialUserFilters.role);
  const [userStatusFilter, setUserStatusFilter] = useState<UserStatusFilter>(initialUserFilters.status);
  const [userPlanFilter, setUserPlanFilter] = useState<UserPlanFilter>(initialUserFilters.plan);
  const [userPlanDurationFilter, setUserPlanDurationFilter] = useState<UserPlanDurationFilter>(initialUserFilters.planDuration);
  const [userSourceFilter, setUserSourceFilter] = useState<UserSourceFilter>(initialUserFilters.source);
  const [userAccountSourceFilter, setUserAccountSourceFilter] = useState<UserAccountSourceFilter>(initialUserFilters.accountSource);
  const [demoEmailTypes, setDemoEmailTypes] = useState<DemoEmailType[]>([]);
  const [testEmailTo, setTestEmailTo] = useState('');
  const [selectedDemoEmailType, setSelectedDemoEmailType] = useState('');
  const [demoEmailSending, setDemoEmailSending] = useState(false);
  const [emailCampaigns, setEmailCampaigns] = useState<EmailCampaignStats[]>([]);
  const [emailCampaignTotals, setEmailCampaignTotals] = useState<EmailCampaignTotals | null>(null);
  const [emailCampaignsLoading, setEmailCampaignsLoading] = useState(false);
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [exportFilter, setExportFilter] = useState<ExportFilter>('current_filters');
  const [exporting, setExporting] = useState<'csv' | 'pdf' | null>(null);
  const [notificationSending, setNotificationSending] = useState(false);
  const [latestTransitSending, setLatestTransitSending] = useState(false);
  const [latestMuhuratSending, setLatestMuhuratSending] = useState(false);
  const [notificationStatsLoading, setNotificationStatsLoading] = useState(false);
  const [notificationStats, setNotificationStats] = useState<NotificationStats | null>(null);
  const [notificationForm, setNotificationForm] = useState({
    targetType: 'anonymous' as NotificationTargetType,
    preference: 'offers' as NotificationPreference,
    title: 'Kundlistar Studio Pro Yearly Plan',
    body: 'Your Kundlistar Studio Pro yearly plan is available at ₹999/year.',
    screen: 'subscription' as NotificationDeepLinkScreen,
    campaign: 'admin_launch_offer'
  });
  const [customEmailSending, setCustomEmailSending] = useState(false);
  const [customEmailForm, setCustomEmailForm] = useState({
    targetType: 'manual_recipients' as EmailTargetType,
    recipients: '',
    subject: 'Kundlistar Studio Pro Yearly Plan',
    body: 'Namaste,\n\nYour Kundlistar Studio Pro yearly plan is available at ₹999/year.\n\nOpen Kundlistar Studio today and continue your professional astrology work.'
  });
  const [manualPaymentOpen, setManualPaymentOpen] = useState(false);
  const [manualPaymentSaving, setManualPaymentSaving] = useState(false);
  const [manualPaymentError, setManualPaymentError] = useState<string | null>(null);
  const [manualPayment, setManualPayment] = useState<ManualPaymentForm>(getDefaultManualPaymentForm());
  const [premiumEditOpen, setPremiumEditOpen] = useState(false);
  const [premiumEditSaving, setPremiumEditSaving] = useState(false);
  const [premiumEditError, setPremiumEditError] = useState<string | null>(null);
  const [premiumEdit, setPremiumEdit] = useState<PremiumEditForm>(getDefaultPremiumEditForm());
  const [manualCities, setManualCities] = useState<ManualCity[]>([]);
  const [manualCitiesLoading, setManualCitiesLoading] = useState(false);
  const [manualCitySaving, setManualCitySaving] = useState(false);
  const [manualCityForm, setManualCityForm] = useState<ManualCityForm>(getDefaultManualCityForm());
  const [selectedUserDetail, setSelectedUserDetail] = useState<AdminUser | null>(null);
  const [createUserOpen, setCreateUserOpen] = useState(false);
  const [newUser, setNewUser] = useState({
    name: '',
    email: '',
    password: '',
    role: 'user',
    subscriptionType: 'free'
  });

  const isAdmin = state.user?.role === 'admin';
  const currentAdminUserId = String((state.user as any)?.id || (state.user as any)?._id || '');

  useEffect(() => {
    if (state.isAuthenticated && isAdmin) {
      loadAdminData();
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.isAuthenticated, isAdmin]);

  useEffect(() => {
    if (state.isAuthenticated && isAdmin && tab === 5 && demoEmailTypes.length === 0) {
      loadDemoEmailTypes();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, state.isAuthenticated, isAdmin, demoEmailTypes.length]);

  useEffect(() => {
    if (state.isAuthenticated && isAdmin && tab === 4) {
      loadEmailCampaigns();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, state.isAuthenticated, isAdmin]);

  useEffect(() => {
    if (state.isAuthenticated && isAdmin && tab === 2) {
      loadNotificationStats();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, state.isAuthenticated, isAdmin]);

  useEffect(() => {
    if (state.isAuthenticated && isAdmin && tab === 1 && !chartsLoaded) {
      loadCharts(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, state.isAuthenticated, isAdmin, chartsLoaded]);

  useEffect(() => {
    if (state.isAuthenticated && isAdmin && tab === 6) {
      loadManualCities();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, state.isAuthenticated, isAdmin]);

  const getUserFilterParams = (
    search = usersSearch,
    filters: UserFilterState = {
      subscriptionStatus: userSubscriptionFilter,
      role: userRoleFilter,
      status: userStatusFilter,
      plan: userPlanFilter,
      planDuration: userPlanDurationFilter,
      source: userSourceFilter,
      accountSource: userAccountSourceFilter
    }
  ) => ({
    search: search || undefined,
    subscriptionStatus: filters.subscriptionStatus === 'all' ? undefined : filters.subscriptionStatus,
    role: filters.role === 'all' ? undefined : filters.role,
    status: filters.status === 'all' ? undefined : filters.status,
    plan: filters.plan === 'all' ? undefined : filters.plan,
    planDuration: filters.planDuration === 'all' ? undefined : filters.planDuration,
    source: filters.source === 'all' ? undefined : filters.source,
    accountSource: filters.accountSource === 'all' ? undefined : filters.accountSource
  });

  const updateUsersUrlParams = (page: number, search: string, filters: UserFilterState) => {
    const nextParams = new URLSearchParams(searchParams.toString());
    nextParams.set('usersPage', String(page));

    if (search) nextParams.set('usersSearch', search);
    else nextParams.delete('usersSearch');

    if (filters.subscriptionStatus !== 'all') nextParams.set('usersSubscription', filters.subscriptionStatus);
    else nextParams.delete('usersSubscription');

    if (filters.role !== 'all') nextParams.set('usersRole', filters.role);
    else nextParams.delete('usersRole');

    if (filters.status !== 'all') nextParams.set('usersStatus', filters.status);
    else nextParams.delete('usersStatus');

    if (filters.plan !== 'all') nextParams.set('usersPlan', filters.plan);
    else nextParams.delete('usersPlan');

    if (filters.planDuration !== 'all') nextParams.set('usersPlanDuration', filters.planDuration);
    else nextParams.delete('usersPlanDuration');

    if (filters.source !== 'all') nextParams.set('usersSource', filters.source);
    else nextParams.delete('usersSource');

    if (filters.accountSource !== 'all') nextParams.set('usersAccountSource', filters.accountSource);
    else nextParams.delete('usersAccountSource');

    setSearchParams(nextParams, { replace: true });
  };

  const getCurrentUserFilters = (): UserFilterState => ({
    subscriptionStatus: userSubscriptionFilter,
    role: userRoleFilter,
    status: userStatusFilter,
    plan: userPlanFilter,
    planDuration: userPlanDurationFilter,
    source: userSourceFilter,
    accountSource: userAccountSourceFilter
  });

  const loadUsers = async (
    page = usersPagination.page,
    search = usersSearch,
    filters = getCurrentUserFilters()
  ) => {
    const normalizedPage = Math.max(page, 1);
    const response = await axios.get('/users/admin/users', {
      params: {
        page: normalizedPage,
        limit: ADMIN_PAGE_SIZE,
        ...getUserFilterParams(search, filters)
      }
    });

    setUsers(response.data.data.users || []);
    setUsersPagination(response.data.data.pagination || emptyPagination);
    updateUsersUrlParams(normalizedPage, search, filters);
  };

  const loadCharts = async (page = chartsPagination.page, search = chartsSearch) => {
    const response = await axios.get('/users/admin/charts', {
      params: {
        page,
        limit: ADMIN_CHARTS_LIMIT,
        search: search || undefined
      }
    });

    setCharts(response.data.data.charts || []);
    setChartsPagination(response.data.data.pagination || emptyPagination);
    setChartsLoaded(true);
  };

  const loadAdminData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [overviewResponse, usersResponse] = await Promise.allSettled([
        axios.get('/users/admin/overview'),
        axios.get('/users/admin/users', {
          params: {
            page: usersPagination.page,
            limit: ADMIN_PAGE_SIZE,
            ...getUserFilterParams()
          }
        })
      ]);

      const errors: string[] = [];

      if (overviewResponse.status === 'fulfilled') {
        setOverview(overviewResponse.value.data.data.overview);
      } else {
        errors.push(overviewResponse.reason?.response?.data?.message || 'Overview failed to load');
      }

      if (usersResponse.status === 'fulfilled') {
        setUsers(usersResponse.value.data.data.users || []);
        setUsersPagination(usersResponse.value.data.data.pagination || emptyPagination);
        updateUsersUrlParams(usersPagination.page, usersSearch, getCurrentUserFilters());
      } else {
        errors.push(usersResponse.reason?.response?.data?.message || 'Users failed to load');
      }

      if (errors.length > 0) {
        setError(errors.join(' | '));
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load admin panel');
    } finally {
      setLoading(false);
    }
  };

  const loadDemoEmailTypes = async () => {
    try {
      const response = await axios.get('/users/admin/test-email-types');
      const nextEmailTypes = response.data.data.emailTypes || [];
      setDemoEmailTypes(nextEmailTypes);
      setSelectedDemoEmailType((current) => current || nextEmailTypes[0]?.id || '');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Demo email templates failed to load');
    }
  };

  const loadEmailCampaigns = async () => {
    try {
      setEmailCampaignsLoading(true);
      const response = await axios.get('/users/admin/email-campaigns', {
        params: { limit: 50 }
      });
      setEmailCampaigns(response.data.data.campaigns || []);
      setEmailCampaignTotals(response.data.data.totals || null);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Email campaigns failed to load');
    } finally {
      setEmailCampaignsLoading(false);
    }
  };

  const loadNotificationStats = async () => {
    try {
      setNotificationStatsLoading(true);
      const response = await axios.get('/notifications/admin/stats');
      setNotificationStats(response.data.data || null);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load notification token stats');
    } finally {
      setNotificationStatsLoading(false);
    }
  };

  const loadManualCities = async () => {
    try {
      setManualCitiesLoading(true);
      const response = await axios.get('/cities/admin/manual');
      setManualCities(response.data.cities || []);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load manual cities');
    } finally {
      setManualCitiesLoading(false);
    }
  };

  const runAction = async (action: () => Promise<void>, message: string) => {
    try {
      setError(null);
      setSuccess(null);
      await action();
      setSuccess(message);
      await axios.get('/users/admin/overview').then((response) => {
        setOverview(response.data.data.overview);
      });
      if (tab === 1) {
        await loadCharts(chartsPagination.page);
      } else {
        await loadUsers(usersPagination.page);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Action failed');
    }
  };

  const updateUser = async (userId: string, payload: Record<string, unknown>, message: string) => {
    await runAction(async () => {
      await axios.patch(`/users/admin/users/${userId}`, payload);
    }, message);
  };

  const deleteUser = async (user: AdminUser) => {
    if (String(user._id) === currentAdminUserId) {
      setError('You cannot delete the current admin account');
      return;
    }

    const confirmed = window.confirm(`Delete ${user.name || user.email}? This will disable the account and remove it from the admin user list.`);
    if (!confirmed) return;

    await runAction(async () => {
      await axios.delete(`/users/admin/users/${user._id}`);
      setUsers((current) => current.filter((item) => item._id !== user._id));
      setSelectedUserIds((current) => current.filter((id) => id !== user._id));
      setSelectedUserDetail((current) => (current?._id === user._id ? null : current));
    }, 'User deleted successfully');
  };

  const getPremiumEndDateForPreset = (
    startDateValue: string,
    durationPreset: PremiumDurationPreset,
    customDaysValue = premiumEdit.customDays
  ) => {
    const start = startDateValue ? new Date(startDateValue) : new Date();
    const end = new Date(start);

    if (Number.isNaN(start.getTime())) {
      return formatDateForInput(new Date());
    }

    if (durationPreset === 'monthly') {
      end.setMonth(end.getMonth() + 1);
    } else if (durationPreset === 'yearly') {
      end.setFullYear(end.getFullYear() + 1);
    } else {
      const days = durationPreset === 'two_days' ? 2 : Number(customDaysValue || 1);
      end.setDate(end.getDate() + Math.max(1, Number.isFinite(days) ? days : 1));
    }

    return formatDateForInput(end);
  };

  const openPremiumEditDialog = (user: AdminUser) => {
    const form = getDefaultPremiumEditForm();
    const startDate = user.subscription?.startDate
      ? formatDateForInput(new Date(user.subscription.startDate))
      : form.startDate;
    const endDate = user.subscription?.endDate
      ? formatDateForInput(new Date(user.subscription.endDate))
      : form.endDate;

    setPremiumEditError(null);
    setPremiumEdit({
      ...form,
      userId: user._id,
      userName: `${user.name} (${user.email})`,
      subscriptionType: (user.subscription?.type === 'basic' || user.subscription?.type === 'premium')
        ? user.subscription.type
        : 'premium',
      durationPreset: user.subscription?.endDate ? 'custom_dates' : 'yearly',
      startDate,
      endDate
    });
    setPremiumEditOpen(true);
  };

  const savePremiumEdit = async () => {
    if (!premiumEdit.userId) {
      setPremiumEditError('Select a user before updating Pro access');
      return;
    }

    const payload: Record<string, unknown> = {
      subscriptionType: premiumEdit.subscriptionType
    };

    if (premiumEdit.subscriptionType !== 'free') {
      payload.durationPreset = premiumEdit.durationPreset;
      payload.startDate = premiumEdit.startDate;

      if (premiumEdit.durationPreset === 'custom_days') {
        const customDays = Number(premiumEdit.customDays);
        if (!Number.isInteger(customDays) || customDays <= 0) {
          setPremiumEditError('Custom days must be a positive whole number');
          return;
        }
        payload.customDays = customDays;
      }

      if (premiumEdit.durationPreset === 'custom_dates') {
        payload.endDate = premiumEdit.endDate;
      }
    }

    try {
      setPremiumEditSaving(true);
      setPremiumEditError(null);
      setError(null);
      setSuccess(null);
      await axios.patch(`/users/admin/users/${premiumEdit.userId}`, payload);
      setSuccess('plan access updated');
      await axios.get('/users/admin/overview').then((response) => {
        setOverview(response.data.data.overview);
      });
      await loadUsers(usersPagination.page);
      setPremiumEditOpen(false);
      setPremiumEdit(getDefaultPremiumEditForm());
    } catch (err: any) {
      setPremiumEditError(err.response?.data?.message || err.response?.data?.error || 'Pro update failed');
    } finally {
      setPremiumEditSaving(false);
    }
  };

  const toggleUserSelection = (userId: string) => {
    setSelectedUserIds((current) => (
      current.includes(userId)
        ? current.filter((id) => id !== userId)
        : [...current, userId]
    ));
  };

  const toggleCurrentPageUsers = (checked: boolean) => {
    const pageIds = users.map((user) => user._id);
    setSelectedUserIds((current) => {
      if (checked) {
        return Array.from(new Set([...current, ...pageIds]));
      }
      return current.filter((id) => !pageIds.includes(id));
    });
  };

  const exportSelectedUsers = async (format: 'csv' | 'pdf') => {
    if (exportFilter === 'selected' && selectedUserIds.length === 0) {
      setError('Select at least one user to export');
      return;
    }

    try {
      setError(null);
      setSuccess(null);
      setExporting(format);

      const response = await axios.post('/users/admin/users/export', {
        userIds: exportFilter === 'selected' ? selectedUserIds : [],
        subscriptionFilter: exportFilter,
        filters: exportFilter === 'current_filters'
          ? {
            search: usersSearch,
            ...getCurrentUserFilters()
          }
          : undefined,
        format
      }, { responseType: 'blob' });

      const disposition = response.headers['content-disposition'] || '';
      const filenameMatch = disposition.match(/filename="?([^"]+)"?/);
      const filename = filenameMatch?.[1] || `kundlistar-users.${format}`;
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      setSuccess(`User ${format.toUpperCase()} export downloaded`);
    } catch (err: any) {
      setError(err.response?.data?.message || 'User export failed');
    } finally {
      setExporting(null);
    }
  };

  const openManualPaymentDialog = (user: AdminUser) => {
    setManualPaymentError(null);
    setManualPayment({
      ...getDefaultManualPaymentForm(),
      userId: user._id,
      userName: `${user.name} (${user.email})`
    });
    setManualPaymentOpen(true);
  };

  const saveManualPayment = async () => {
    if (!manualPayment.userId) {
      setManualPaymentError('Select a user before adding payment');
      return;
    }

    const requiredFields: Array<[keyof ManualPaymentForm, string]> = [
      ['amount', 'Amount'],
      ['paymentMethod', 'Payment method'],
      ['receipt', 'Receipt'],
      ['phone', 'Phone'],
      ['startDate', 'Start date'],
      ['endDate', 'End date'],
      ['notes', 'Admin notes']
    ];
    const missingField = requiredFields.find(([field]) => !String(manualPayment[field] || '').trim());
    if (missingField) {
      setManualPaymentError(`${missingField[1]} is required for manual payment`);
      return;
    }

    if (manualPayment.paymentMethod === 'unknown') {
      setManualPaymentError('Select a real payment method for manual payment');
      return;
    }

    const amount = Number(manualPayment.amount);
    if (!Number.isFinite(amount) || amount <= 0) {
      setManualPaymentError('Amount must be greater than 0');
      return;
    }

    try {
      setError(null);
      setSuccess(null);
      setManualPaymentError(null);
      setManualPaymentSaving(true);
      await axios.post(`/users/admin/users/${manualPayment.userId}/payments/manual`, {
        subscriptionType: manualPayment.subscriptionType,
        amount: Number(manualPayment.amount || 0),
        currency: manualPayment.currency,
        paymentMethod: manualPayment.paymentMethod,
        receipt: manualPayment.receipt,
        startDate: manualPayment.startDate,
        endDate: manualPayment.endDate,
        notes: manualPayment.notes,
        phone: manualPayment.phone
      });
      setManualPaymentOpen(false);
      setManualPayment(getDefaultManualPaymentForm());
      setSuccess('Manual payment added and user access updated');
      await loadAdminData();
    } catch (err: any) {
      const message = err.response?.data?.message || err.response?.data?.error || 'Manual payment failed';
      setManualPaymentError(message);
    } finally {
      setManualPaymentSaving(false);
    }
  };

  const updateChartVisibility = async (chartId: string, visibilityStatus: 'shown' | 'deleted') => {
    await runAction(async () => {
      await axios.patch(`/users/admin/charts/${chartId}/visibility`, { visibilityStatus });
    }, `Chart ${visibilityStatus === 'deleted' ? 'hidden' : 'restored'} successfully`);
  };

  const archiveAnonymousCharts = async () => {
    await runAction(async () => {
      const response = await axios.post('/users/admin/charts/archive-anonymous');
      setSuccess(response.data.message || 'Anonymous charts archived successfully');
    }, 'Anonymous charts archived successfully');
  };

  const createUser = async () => {
    if (!newUser.name || !newUser.email || !newUser.password) {
      setError('Name, email, and password are required');
      return;
    }

    await runAction(async () => {
      await axios.post('/users/admin/users', newUser);
      setNewUser({
        name: '',
        email: '',
        password: '',
        role: 'user',
        subscriptionType: 'free'
      });
      setCreateUserOpen(false);
    }, 'User created successfully');
  };

  const createManualCity = async () => {
    const latitude = Number(manualCityForm.latitude);
    const longitude = Number(manualCityForm.longitude);

    if (!manualCityForm.name.trim()) {
      setError('City name is required');
      return;
    }

    if (!/^[A-Za-z]{2}$/.test(manualCityForm.countryCode.trim())) {
      setError('Country code must be 2 letters, like IN or US');
      return;
    }

    if (!Number.isFinite(latitude) || latitude < -90 || latitude > 90) {
      setError('Latitude must be between -90 and 90');
      return;
    }

    if (!Number.isFinite(longitude) || longitude < -180 || longitude > 180) {
      setError('Longitude must be between -180 and 180');
      return;
    }

    try {
      setError(null);
      setSuccess(null);
      setManualCitySaving(true);
      await axios.post('/cities/admin/manual', {
        name: manualCityForm.name.trim(),
        countryCode: manualCityForm.countryCode.trim().toUpperCase(),
        countryName: manualCityForm.countryName.trim(),
        stateName: manualCityForm.stateName.trim(),
        stateCode: manualCityForm.stateCode.trim().toUpperCase(),
        latitude,
        longitude,
        timezone: manualCityForm.timezone.trim(),
        population: Number(manualCityForm.population || 0),
        native: manualCityForm.native.trim(),
        translationHi: manualCityForm.translationHi.trim()
      });
      setManualCityForm(getDefaultManualCityForm());
      setSuccess('Manual city added successfully. It will now appear in city search.');
      await loadManualCities();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Manual city creation failed');
    } finally {
      setManualCitySaving(false);
    }
  };

  const sendAdminNotification = async () => {
    if (!notificationForm.title.trim() || !notificationForm.body.trim()) {
      setError('Notification title and body are required');
      return;
    }

    if (notificationForm.targetType === 'selected_users' && selectedUserIds.length === 0) {
      setError('Select at least one user from the Users tab or choose another target type');
      return;
    }

    try {
      setError(null);
      setSuccess(null);
      setNotificationSending(true);

      const data: Record<string, string> = {};
      const selectedDeepLink = NOTIFICATION_DEEP_LINK_SCREENS.find((screen) => screen.value === notificationForm.screen);
      if (selectedDeepLink) {
        data.screen = selectedDeepLink.value;
        data.route = selectedDeepLink.route;
      }
      if (notificationForm.campaign.trim()) data.campaign = notificationForm.campaign.trim();

      const response = await axios.post('/notifications/admin/send', {
        targetType: notificationForm.targetType,
        userIds: notificationForm.targetType === 'selected_users' ? selectedUserIds : [],
        preference: notificationForm.preference,
        title: notificationForm.title.trim(),
        body: notificationForm.body.trim(),
        data
      });

      const result = response.data.data;
      const ticketStatuses = result.ticketSummary?.statuses
        ? Object.entries(result.ticketSummary.statuses).map(([key, value]) => `${key}: ${value}`).join(', ')
        : 'no ticket status';
      const firstTicketError = result.ticketSummary?.errors?.[0];
      setSuccess(
        `Notification sent. Matched ${result.matchedTokens} tokens, valid Expo tokens: ${result.validExpoTokens}. Expo tickets: ${ticketStatuses}.` +
        (firstTicketError ? ` First error: ${firstTicketError.code} - ${firstTicketError.message}` : '')
      );
      await loadNotificationStats();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Notification failed to send');
    } finally {
      setNotificationSending(false);
    }
  };

  const sendLatestUpcomingTransitPush = async () => {
    if (notificationForm.targetType === 'selected_users' && selectedUserIds.length === 0) {
      setError('Select at least one user from the Users tab or choose another target type');
      return;
    }

    try {
      setError(null);
      setSuccess(null);
      setLatestTransitSending(true);

      const response = await axios.post('/notifications/admin/send-latest-transit', {
        targetType: notificationForm.targetType,
        userIds: notificationForm.targetType === 'selected_users' ? selectedUserIds : []
      });
      const result = response.data.data;
      const eventDate = result.eventDate ? formatDisplayDateTime(result.eventDate) : 'unknown time';

      setSuccess(
        `Latest upcoming transit push sent for "${result.eventTitle}" scheduled at ${eventDate}. Audience: ${result.targetType}. Matched tokens: ${result.matchedTokens}. Delivered push attempts: ${result.sent}.`
      );
      await loadNotificationStats();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Latest upcoming transit push failed');
    } finally {
      setLatestTransitSending(false);
    }
  };

  const sendLatestMuhuratPush = async () => {
    if (notificationForm.targetType === 'selected_users' && selectedUserIds.length === 0) {
      setError('Select at least one user from the Users tab or choose another target type');
      return;
    }

    try {
      setError(null);
      setSuccess(null);
      setLatestMuhuratSending(true);

      const response = await axios.post('/notifications/admin/send-latest-muhurat', {
        targetType: notificationForm.targetType,
        userIds: notificationForm.targetType === 'selected_users' ? selectedUserIds : []
      });
      const result = response.data.data;
      const start = result.start ? formatDisplayDateTime(result.start) : 'unknown time';
      const end = result.end ? formatDisplayDateTime(result.end) : 'unknown time';

      setSuccess(
        `Latest Muhurat push sent for ${result.choghadiya || 'Muhurat'} from ${start} to ${end}. Audience: ${result.targetType}. Matched tokens: ${result.matchedTokens || 0}. Delivered push attempts: ${result.validExpoTokens || 0}.`
      );
      await loadNotificationStats();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Latest Muhurat push failed');
    } finally {
      setLatestMuhuratSending(false);
    }
  };

  const sendCustomEmailCampaign = async () => {
    if (!customEmailForm.subject.trim() || !customEmailForm.body.trim()) {
      setError('Email subject and body are required');
      return;
    }

    if (customEmailForm.targetType === 'selected_users' && selectedUserIds.length === 0) {
      setError('Select at least one user from the Users tab or choose another target type');
      return;
    }

    if (customEmailForm.targetType === 'manual_recipients' && !customEmailForm.recipients.trim()) {
      setError('Enter at least one recipient email');
      return;
    }

    try {
      setError(null);
      setSuccess(null);
      setCustomEmailSending(true);

      const response = await axios.post('/users/admin/send-custom-email', {
        targetType: customEmailForm.targetType,
        userIds: customEmailForm.targetType === 'selected_users' ? selectedUserIds : [],
        recipients: customEmailForm.targetType === 'manual_recipients' ? customEmailForm.recipients.trim() : undefined,
        subject: customEmailForm.subject.trim(),
        body: customEmailForm.body.trim()
      });

      const result = response.data.data;
      const summary = result.summary || {};
      const audienceSummary = result.targetType === 'manual_recipients'
        ? `Manual recipients: ${result.uniqueRecipients}.`
        : `Matched ${result.matchedUsers} users, unique recipients: ${result.uniqueRecipients}.`;
      setSuccess(
        `Email delivery completed. ${audienceSummary} Sent: ${summary.sent || 0}. Failed: ${summary.failed || 0}. Skipped: ${summary.skipped || 0}. Campaign ID: ${result.campaignId || 'n/a'}.`
      );
      await loadEmailCampaigns();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Custom email campaign failed');
    } finally {
      setCustomEmailSending(false);
    }
  };

  const sendDemoEmailToRecipient = async (type: string = selectedDemoEmailType) => {
    if (!testEmailTo.trim()) {
      setError('Recipient email is required');
      return;
    }

    if (!type) {
      setError('Select an email template to send');
      return;
    }

    try {
      setError(null);
      setSuccess(null);
      setDemoEmailSending(true);
      const response = await axios.post('/users/admin/test-email', {
        to: testEmailTo.trim(),
        type
      });
      setSuccess(response.data.message || 'Demo email sent successfully');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Demo email failed to send');
    } finally {
      setDemoEmailSending(false);
    }
  };

  const applyUsersSearch = async () => {
    const nextSearch = usersSearchInput.trim();
    setUsersSearch(nextSearch);
    await loadUsers(1, nextSearch);
  };

  const clearUsersSearch = async () => {
    setUsersSearchInput('');
    setUsersSearch('');
    await loadUsers(1, '');
  };

  const applyUserFilters = async () => {
    await loadUsers(1, usersSearch, getCurrentUserFilters());
  };

  const clearUserFilters = async () => {
    const clearedFilters: UserFilterState = {
      subscriptionStatus: 'all',
      role: 'all',
      status: 'all',
      plan: 'all',
      planDuration: 'all',
      source: 'all',
      accountSource: 'studio'
    };
    setUsersSearchInput('');
    setUsersSearch('');
    setUserSubscriptionFilter(clearedFilters.subscriptionStatus);
    setUserRoleFilter(clearedFilters.role);
    setUserStatusFilter(clearedFilters.status);
    setUserPlanFilter(clearedFilters.plan);
    setUserPlanDurationFilter(clearedFilters.planDuration);
    setUserSourceFilter(clearedFilters.source);
    setUserAccountSourceFilter(clearedFilters.accountSource);
    await loadUsers(1, '', clearedFilters);
  };

  const applyChartsSearch = async () => {
    const nextSearch = chartsSearchInput.trim();
    setChartsSearch(nextSearch);
    await loadCharts(1, nextSearch);
  };

  const clearChartsSearch = async () => {
    setChartsSearchInput('');
    setChartsSearch('');
    await loadCharts(1, '');
  };

  const formatDate = (value?: string, includeTime = false) => {
    if (!value) return '-';
    const date = new Date(value);

    if (includeTime) {
      return formatDisplayDateTime(date);
    }

    return formatDisplayDate(date);
  };

  const getChartSourceLabel = (source?: AdminChart['createdFrom']) => {
    if (source === 'mobile') return 'Mobile App';
    if (source === 'web') return 'Website';
    if (source === 'admin') return 'Admin';
    if (source === 'api') return 'API';
    return 'Unknown';
  };

  const getChartSourceColor = (source?: AdminChart['createdFrom']) => {
    if (source === 'mobile') return 'secondary';
    if (source === 'web') return 'info';
    if (source === 'admin') return 'warning';
    if (source === 'api') return 'default';
    return 'default';
  };

  const getEmailCampaignStatusColor = (status?: EmailCampaignStats['status']) => {
    if (status === 'completed') return 'success';
    if (status === 'running') return 'info';
    if (status === 'paused') return 'warning';
    if (status === 'queued') return 'default';
    return 'default';
  };

  const getEmailCampaignProgress = (campaign: EmailCampaignStats) => {
    const total = Number(campaign.totalEmails || 0);
    if (total <= 0) return 0;
    return Math.min(100, Math.round(((campaign.counts.sent + campaign.counts.failed) / total) * 100));
  };

  if (!state.isAuthenticated) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="warning">Please login to access the admin panel.</Alert>
      </Container>
    );
  }

  if (!isAdmin) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error">Only admin users can access this page.</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" gutterBottom>
            एडमिन पैनल / Admin Panel
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage users, payments, notifications, and all saved charts.
          </Typography>
        </Box>
        <Button variant="outlined" startIcon={<Refresh />} onClick={loadAdminData}>
          Refresh
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid size={{ xs: 12, sm: 6, md: 2 }}>
              <Card>
                <CardContent>
                  <Typography variant="subtitle2">Local Users</Typography>
                  <Typography variant="h5">{overview?.localUsers ?? 0}</Typography>
                  <Typography variant="caption" color="text.secondary">Studio users only</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 2 }}>
              <Card>
                <CardContent>
                  <Typography variant="subtitle2">All Users</Typography>
                  <Typography variant="h5">{overview?.totalUsers ?? 0}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 2 }}>
              <Card>
                <CardContent>
                  <Typography variant="subtitle2">Studio Pro Users</Typography>
                  <Typography variant="h5">{overview?.localProUsers ?? overview?.premiumUsers ?? 0}</Typography>
                  <Typography variant="caption" color="text.secondary">Native paid Studio users</Typography>
                </CardContent>
              </Card>
            </Grid>
            {/* <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
              <Card>
                <CardContent>
                  <Typography variant="subtitle2">Admins</Typography>
                  <Typography variant="h5">{overview?.adminUsers ?? 0}</Typography>
                </CardContent>
              </Card>
            </Grid> */}
            <Grid size={{ xs: 12, sm: 6, md: 2 }}>
              <Card>
                <CardContent>
                  <Typography variant="subtitle2">Studio Charts Today</Typography>
                  <Typography variant="h5">{overview?.localChartsCreatedToday ?? 0}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Created in Studio since 12:00 AM IST
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            {/* <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
              <Card>
                <CardContent>
                  <Typography variant="subtitle2">Pending Requests</Typography>
                  <Typography variant="h5">{overview?.pendingRequests ?? 0}</Typography>
                </CardContent>
              </Card>
            </Grid> */}
            <Grid size={{ xs: 12, sm: 6, md: 2 }}>
              <Card>
                <CardContent>
                  <Typography variant="subtitle2">ALL Charts</Typography>
                  <Typography variant="h5">
                    {(overview?.totalCharts ?? 0) + (overview?.anonymousCharts ?? 0)}

                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                  User Chart: <b>{overview?.totalCharts ?? 0}</b> | Archived: <b>{overview?.anonymousCharts ?? 0}</b>
                  </Typography>
                  
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 2 }}>
              <Card>
                <CardContent>
                  <Typography variant="subtitle2">All Kundlistar Studio Charts</Typography>
                  <Typography variant="h5">
                    {(overview?.studioCharts ?? 0) + (overview?.anonymousCharts ?? 0)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    User Chart: <b>{overview?.studioCharts ?? 0}</b> | Archived: <b>{overview?.anonymousCharts ?? 0}</b>
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Paper sx={{ mb: 2 }}>
            <Tabs value={tab} onChange={(_, value) => setTab(value)} variant="scrollable" scrollButtons="auto">
              <Tab icon={<AdminPanelSettings />} iconPosition="start" label="Users" />
              <Tab icon={<TableChart />} iconPosition="start" label="All Charts" />
              <Tab icon={<NotificationsActive />} iconPosition="start" label="Notifications" />
              <Tab icon={<Email />} iconPosition="start" label="Emails" />
              <Tab icon={<Campaign />} iconPosition="start" label="Email Campaigns" />
              <Tab icon={<Email />} iconPosition="start" label="Test Emails" />
              <Tab icon={<LocationCity />} iconPosition="start" label="Manual Cities" />
            </Tabs>
          </Paper>

          {tab === 0 && (
            <>
              <Card sx={{ mb: 2 }}>
                <CardContent>
                  <Grid container spacing={1.5} alignItems="center">
                    <Grid size={{ xs: 12, md: 8 }}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Search users in database"
                        placeholder="Search all Studio users by name or email"
                        value={usersSearchInput}
                        onChange={(e) => setUsersSearchInput(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            applyUsersSearch();
                          }
                        }}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 4 }}>
                      <Grid container spacing={1}>
                        <Grid size={{ xs: 6, sm: 4 }}>
                          <Button fullWidth variant="contained" onClick={applyUsersSearch}>Search</Button>
                        </Grid>
                        <Grid size={{ xs: 6, sm: 4 }}>
                          <Button fullWidth variant="outlined" onClick={clearUsersSearch} disabled={!usersSearch && !usersSearchInput}>Clear</Button>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 4 }}>
                          <Button fullWidth variant="contained" startIcon={<PersonAdd />} onClick={() => setCreateUserOpen(true)}>
                          Create User
                        </Button>
                        </Grid>
                      </Grid>
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                      <Grid container spacing={1} alignItems="center">
                        <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                        <TextField
                          select
                          fullWidth
                          size="small"
                          label="Pro status"
                          value={userSubscriptionFilter}
                          onChange={(e) => setUserSubscriptionFilter(e.target.value as UserSubscriptionFilter)}
                        >
                          <MenuItem value="all">All users</MenuItem>
                          <MenuItem value="premium">Pro users</MenuItem>
                          <MenuItem value="non_premium">Non-Pro users</MenuItem>
                        </TextField>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 1.7 }}>
                        <TextField
                          select
                          fullWidth
                          size="small"
                          label="Role"
                          value={userRoleFilter}
                          onChange={(e) => setUserRoleFilter(e.target.value as UserRoleFilter)}
                        >
                          <MenuItem value="all">All roles</MenuItem>
                          <MenuItem value="user">User</MenuItem>
                          <MenuItem value="astrologer">Astrologer</MenuItem>
                          <MenuItem value="admin">Admin</MenuItem>
                        </TextField>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 1.7 }}>
                        <TextField
                          select
                          fullWidth
                          size="small"
                          label="Status"
                          value={userStatusFilter}
                          onChange={(e) => setUserStatusFilter(e.target.value as UserStatusFilter)}
                        >
                          <MenuItem value="all">All status</MenuItem>
                          <MenuItem value="active">Active</MenuItem>
                          <MenuItem value="disabled">Disabled</MenuItem>
                        </TextField>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 1.7 }}>
                        <TextField
                          select
                          fullWidth
                          size="small"
                          label="Plan"
                          value={userPlanFilter}
                          onChange={(e) => setUserPlanFilter(e.target.value as UserPlanFilter)}
                        >
                          <MenuItem value="all">All plans</MenuItem>
                          <MenuItem value="free">Free</MenuItem>
                          <MenuItem value="basic">Basic</MenuItem>
                          <MenuItem value="premium">Pro</MenuItem>
                        </TextField>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 1.9 }}>
                        <TextField
                          select
                          fullWidth
                          size="small"
                          label="Plan duration"
                          value={userPlanDurationFilter}
                          onChange={(e) => setUserPlanDurationFilter(e.target.value as UserPlanDurationFilter)}
                        >
                          <MenuItem value="all">Any duration</MenuItem>
                          <MenuItem value="monthly">Monthly plan</MenuItem>
                          <MenuItem value="yearly">Yearly plan</MenuItem>
                        </TextField>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 1.9 }}>
                        <TextField
                          select
                          fullWidth
                          size="small"
                          label="User source"
                          value={userAccountSourceFilter}
                          onChange={(e) => setUserAccountSourceFilter(e.target.value as UserAccountSourceFilter)}
                        >
                          <MenuItem value="all">All users</MenuItem>
                          <MenuItem value="studio">Studio users</MenuItem>
                          <MenuItem value="kundlistar">Kundlistar users</MenuItem>
                        </TextField>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 1.9 }}>
                        <TextField
                          select
                          fullWidth
                          size="small"
                          label="Payment source"
                          value={userSourceFilter}
                          onChange={(e) => setUserSourceFilter(e.target.value as UserSourceFilter)}
                        >
                          <MenuItem value="all">All sources</MenuItem>
                          <MenuItem value="manual">Manual</MenuItem>
                          <MenuItem value="revenuecat_ios">RevenueCat iOS</MenuItem>
                        </TextField>
                        </Grid>
                        <Grid size={{ xs: 6, sm: 3, md: 1.05 }}>
                          <Button fullWidth variant="outlined" onClick={applyUserFilters}>Apply</Button>
                        </Grid>
                        <Grid size={{ xs: 6, sm: 3, md: 1.05 }}>
                          <Button fullWidth variant="text" onClick={clearUserFilters}>Clear All</Button>
                        </Grid>
                        <Grid size={{ xs: 12 }}>
                          <Typography variant="caption" color="text.secondary">
                          Matched {usersPagination.total} users in DB. Showing the {ADMIN_PAGE_SIZE} most recent matches per page.
                        </Typography>
                        </Grid>
                      </Grid>
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                      <Grid container spacing={1} alignItems="center">
                        <Grid size={{ xs: 12, sm: 4, md: 1.3 }}>
                        <Chip
                          icon={<Group />}
                          label={`${selectedUserIds.length} selected`}
                          color={selectedUserIds.length > 0 ? 'primary' : 'default'}
                          variant={selectedUserIds.length > 0 ? 'filled' : 'outlined'}
                          sx={{ width: { xs: '100%', sm: 'auto' }, justifyContent: 'center' }}
                        />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 8, md: 2.6 }}>
                        <TextField
                          select
                          fullWidth
                          size="small"
                          label="Export users"
                          value={exportFilter}
                          onChange={(e) => setExportFilter(e.target.value as ExportFilter)}
                        >
                          <MenuItem value="current_filters">Current filters from DB</MenuItem>
                          <MenuItem value="all_users">All users from DB</MenuItem>
                          <MenuItem value="premium">All Pro users</MenuItem>
                          <MenuItem value="non_premium">All non-Pro users</MenuItem>
                          <MenuItem value="selected">Selected users only</MenuItem>
                        </TextField>
                        </Grid>
                        <Grid size={{ xs: 6, sm: 3, md: 1.15 }}>
                        <Button
                          fullWidth
                          variant="outlined"
                          startIcon={exporting === 'csv' ? <CircularProgress size={16} /> : <Download />}
                          disabled={(exportFilter === 'selected' && selectedUserIds.length === 0) || Boolean(exporting)}
                          onClick={() => exportSelectedUsers('csv')}
                        >
                          CSV
                        </Button>
                        </Grid>
                        <Grid size={{ xs: 6, sm: 3, md: 1.15 }}>
                        <Button
                          fullWidth
                          variant="outlined"
                          startIcon={exporting === 'pdf' ? <CircularProgress size={16} /> : <PictureAsPdf />}
                          disabled={(exportFilter === 'selected' && selectedUserIds.length === 0) || Boolean(exporting)}
                          onClick={() => exportSelectedUsers('pdf')}
                        >
                          PDF
                        </Button>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 4, md: 1.7 }}>
                        <Button
                          fullWidth
                          variant="text"
                          disabled={selectedUserIds.length === 0}
                          onClick={() => setSelectedUserIds([])}
                        >
                          Clear Selection
                        </Button>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
              <TableContainer component={Paper}>
                <Table>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={users.length > 0 && users.every((user) => selectedUserIds.includes(user._id))}
                        indeterminate={users.some((user) => selectedUserIds.includes(user._id)) && !users.every((user) => selectedUserIds.includes(user._id))}
                        onChange={(event) => toggleCurrentPageUsers(event.target.checked)}
                      />
                    </TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>User Source</TableCell>
                    <TableCell>Account Created</TableCell>
                    <TableCell>Role</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Subscription</TableCell>
                    <TableCell>Charts</TableCell>
                    <TableCell>AI Reports</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {users.map((user) => (
                    <TableRow
                      key={user._id}
                      hover
                      onClick={() => setSelectedUserDetail(user)}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedUserIds.includes(user._id)}
                          onClick={(event) => event.stopPropagation()}
                          onChange={() => toggleUserSelection(user._id)}
                        />
                      </TableCell>
                      <TableCell>{user.name}</TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>
                        <Chip
                          label={user.accountSource === 'kundlistar' ? 'Kundlistar User' : 'Studio User'}
                          color={user.accountSource === 'kundlistar' ? 'secondary' : 'primary'}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>{formatDate(user.createdAt, true)}</TableCell>
                      <TableCell>
                        <Chip label={user.role} color={user.role === 'admin' ? 'secondary' : 'default'} size="small" />
                      </TableCell>
                      <TableCell>
                        <Chip label={user.isActive ? 'Active' : 'Disabled'} color={user.isActive ? 'success' : 'error'} size="small" />
                      </TableCell>
                      <TableCell>{user.subscription?.type === 'premium' ? 'Pro' : user.subscription?.type || 'free'}</TableCell>
                      <TableCell>{user.subscription?.chartsRemaining ?? 'Unlimited'}</TableCell>
                      <TableCell>
                        <Stack spacing={0.5}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {user.aiReportsCurrentMonth?.generated ?? 0} generated
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Used {user.aiReportsCurrentMonth?.quotaUsed ?? 0}
                            {' / '}
                            {user.aiReportsCurrentMonth?.limit === null
                              ? 'Unlimited'
                              : user.aiReportsCurrentMonth?.limit ?? '-'}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={0.5}>
                          <Tooltip title={hasPremiumSubscription(user) ? 'Edit Pro' : 'Set Pro'} arrow>
                            <IconButton
                              size="small"
                              color="primary"
                              aria-label={hasPremiumSubscription(user) ? 'Edit Pro' : 'Set Pro'}
                              onClick={(event) => {
                                event.stopPropagation();
                                openPremiumEditDialog(user);
                              }}
                            >
                              <WorkspacePremium fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Add Payment" arrow>
                            <IconButton
                              size="small"
                              color="primary"
                              aria-label="Add Payment"
                              onClick={(event) => {
                                event.stopPropagation();
                                openManualPaymentDialog(user);
                              }}
                            >
                              <ReceiptLong fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={user.isActive ? 'Disable' : 'Enable'} arrow>
                            <IconButton
                              size="small"
                              color={user.isActive ? 'warning' : 'success'}
                              aria-label={user.isActive ? 'Disable' : 'Enable'}
                              onClick={(event) => {
                                event.stopPropagation();
                                updateUser(user._id, { isActive: !user.isActive }, 'User status updated');
                              }}
                            >
                              {user.isActive ? <Block fontSize="small" /> : <CheckCircle fontSize="small" />}
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={String(user._id) === currentAdminUserId ? 'Cannot delete current admin' : 'Delete'} arrow>
                            <span onClick={(event) => event.stopPropagation()}>
                              <IconButton
                                size="small"
                                color="error"
                                aria-label="Delete"
                                disabled={String(user._id) === currentAdminUserId}
                                onClick={(event) => {
                                  event.stopPropagation();
                                  deleteUser(user);
                                }}
                              >
                                <Delete fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={usersPagination.total}
              page={Math.max(usersPagination.page - 1, 0)}
              onPageChange={(_, nextPage) => loadUsers(nextPage + 1)}
              rowsPerPage={ADMIN_PAGE_SIZE}
              rowsPerPageOptions={[ADMIN_PAGE_SIZE]}
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} of ${count}`}
            />
            </>
          )}

          {tab === 1 && (
            <>
              <Card sx={{ mb: 2 }}>
                <CardContent>
                  <Grid container spacing={2} alignItems="center">
                    <Grid size={{ xs: 12, md: 7 }}>
                      <TextField
                        fullWidth
                        label="Search charts in database"
                        placeholder="Search by chart name, user, email, city, country, type, source, or status"
                        value={chartsSearchInput}
                        onChange={(e) => setChartsSearchInput(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            applyChartsSearch();
                          }
                        }}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 5 }}>
                      <Stack direction="row" spacing={1} flexWrap="wrap">
                        <Button variant="contained" onClick={applyChartsSearch}>Search DB</Button>
                        <Button variant="outlined" onClick={clearChartsSearch} disabled={!chartsSearch && !chartsSearchInput}>Clear</Button>
                        <Button variant="outlined" color="warning" onClick={archiveAnonymousCharts}>
                          Archive Anonymous
                        </Button>
                        <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
                          Showing latest {ADMIN_CHARTS_LIMIT} only.
                        </Typography>
                      </Stack>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
              <TableContainer component={Paper}>
                <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Source</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>User</TableCell>
                    <TableCell>Birth Place</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {charts.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center">No charts found.</TableCell>
                    </TableRow>
                  ) : charts.map((chart) => (
                    <TableRow key={chart._id} hover>
                      <TableCell>{displayChartName(chart.birthDetails?.name)}</TableCell>
                      <TableCell>
                        <Chip
                          label={chart.chartType === 'horary' ? 'Horary' : 'Individual'}
                          size="small"
                          color={chart.chartType === 'horary' ? 'warning' : 'primary'}
                          variant={chart.chartType === 'horary' ? 'filled' : 'outlined'}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={getChartSourceLabel(chart.createdFrom)}
                          size="small"
                          color={getChartSourceColor(chart.createdFrom) as any}
                          variant={chart.createdFrom ? 'filled' : 'outlined'}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={chart.visibilityStatus === 'deleted' ? 'Deleted' : 'Shown'}
                          size="small"
                          color={chart.visibilityStatus === 'deleted' ? 'error' : 'success'}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{chart.userId?.name || 'Anonymous'}</Typography>
                        <Typography variant="caption" color="text.secondary">{chart.userId?.email || '-'}</Typography>
                      </TableCell>
                      <TableCell>
                        {chart.birthDetails?.placeOfBirth?.city}, {chart.birthDetails?.placeOfBirth?.country}
                      </TableCell>
                      <TableCell>{formatDate(chart.createdAt, true)}</TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1}>
                          <Button size="small" variant="outlined" startIcon={<Visibility />} onClick={() => navigate(`/chart/${chart._id}`)}>
                            View
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            color={chart.visibilityStatus === 'deleted' ? 'success' : 'error'}
                            startIcon={chart.visibilityStatus === 'deleted' ? <Visibility /> : <Delete />}
                            onClick={() => updateChartVisibility(chart._id, chart.visibilityStatus === 'deleted' ? 'shown' : 'deleted')}
                          >
                            {chart.visibilityStatus === 'deleted' ? 'Show' : 'Hide'}
                          </Button>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            </>
          )}

          {tab === 2 && (
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 5 }}>
                <Card>
                  <CardContent>
                    <Stack spacing={2}>
                      <Box>
                        <Typography variant="h6" gutterBottom>
                          Send Push Notification
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Send onboarding, launch offer, or product update notifications to app devices that have granted permission.
                        </Typography>
                      </Box>

                      <TextField
                        select
                        fullWidth
                        label="Target users"
                        value={notificationForm.targetType}
                        onChange={(event) => setNotificationForm({ ...notificationForm, targetType: event.target.value as NotificationTargetType })}
                      >
                        <MenuItem value="anonymous">Anonymous non-registered users</MenuItem>
                        <MenuItem value="registered">All registered users</MenuItem>
                        <MenuItem value="premium">Pro users</MenuItem>
                        <MenuItem value="non_premium">Registered non-Pro users</MenuItem>
                        <MenuItem value="all">All devices</MenuItem>
                        <MenuItem value="selected_users">Selected users from Users tab</MenuItem>
                      </TextField>

                      {notificationForm.targetType === 'selected_users' && (
                        <Alert severity={selectedUserIds.length > 0 ? 'info' : 'warning'}>
                          {selectedUserIds.length} selected user{selectedUserIds.length === 1 ? '' : 's'} will receive this notification.
                        </Alert>
                      )}

                      <TextField
                        select
                        fullWidth
                        label="Preference bucket"
                        value={notificationForm.preference}
                        onChange={(event) => setNotificationForm({ ...notificationForm, preference: event.target.value as NotificationPreference })}
                      >
                        <MenuItem value="offers">Offers</MenuItem>
                        <MenuItem value="productUpdates">Product updates</MenuItem>
                        <MenuItem value="transitAlerts">Transit alerts</MenuItem>
                        <MenuItem value="dailyHoroscope">Daily horoscope</MenuItem>
                        <MenuItem value="muhurat">Muhurat</MenuItem>
                      </TextField>

                      <TextField
                        fullWidth
                        label="Title"
                        value={notificationForm.title}
                        onChange={(event) => setNotificationForm({ ...notificationForm, title: event.target.value })}
                      />

                      <TextField
                        fullWidth
                        multiline
                        minRows={3}
                        label="Body"
                        value={notificationForm.body}
                        onChange={(event) => setNotificationForm({ ...notificationForm, body: event.target.value })}
                      />

                      <Grid container spacing={1.5}>
                        <Grid size={{ xs: 12, md: 6 }}>
                          <TextField
                            select
                            fullWidth
                            label="Mobile app screen"
                            value={notificationForm.screen}
                            onChange={(event) => setNotificationForm({
                              ...notificationForm,
                              screen: event.target.value as NotificationDeepLinkScreen
                            })}
                          >
                            {NOTIFICATION_DEEP_LINK_SCREENS.map((screen) => (
                              <MenuItem key={screen.value} value={screen.value}>
                                {screen.label}
                              </MenuItem>
                            ))}
                          </TextField>
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                          <TextField
                            fullWidth
                            label="Campaign"
                            placeholder="launch_offer"
                            value={notificationForm.campaign}
                            onChange={(event) => setNotificationForm({ ...notificationForm, campaign: event.target.value })}
                          />
                        </Grid>
                      </Grid>

                      <Button
                        variant="contained"
                        startIcon={notificationSending ? <CircularProgress size={18} color="inherit" /> : <Send />}
                        disabled={notificationSending}
                        onClick={sendAdminNotification}
                      >
                        {notificationSending ? 'Sending...' : 'Send Notification'}
                      </Button>

                      <Button
                        variant="outlined"
                        color="success"
                        startIcon={latestTransitSending ? <CircularProgress size={18} color="inherit" /> : <NotificationsActive />}
                        disabled={latestTransitSending}
                        onClick={sendLatestUpcomingTransitPush}
                      >
                        {latestTransitSending ? 'Sending Transit...' : 'Send Latest Upcoming Transit Push'}
                      </Button>

                      <Button
                        variant="outlined"
                        color="warning"
                        startIcon={latestMuhuratSending ? <CircularProgress size={18} color="inherit" /> : <NotificationsActive />}
                        disabled={latestMuhuratSending}
                        onClick={sendLatestMuhuratPush}
                      >
                        {latestMuhuratSending ? 'Sending Muhurat...' : 'Send Latest Muhurat Push'}
                      </Button>
                    </Stack>
                  </CardContent>
                </Card>

              </Grid>

              <Grid size={{ xs: 12, md: 7 }}>
                <Stack spacing={2}>
                <Card>
                  <CardContent>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                      <Box>
                        <Typography variant="h6" gutterBottom>
                          Push Token Inventory
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          If these counts are zero, open the mobile app on a physical device and allow notifications.
                        </Typography>
                      </Box>
                      <Button
                        variant="outlined"
                        startIcon={notificationStatsLoading ? <CircularProgress size={16} /> : <Refresh />}
                        onClick={loadNotificationStats}
                        disabled={notificationStatsLoading}
                      >
                        Refresh
                      </Button>
                    </Stack>

                    {notificationStats ? (
                      <Grid container spacing={1.5}>
                        {[
                          ['Total saved', notificationStats.total],
                          ['Active', notificationStats.active],
                          ['Anonymous', notificationStats.anonymous],
                          ['Registered', notificationStats.registered],
                          ['Pro', notificationStats.premium],
                          ['Non-Pro', notificationStats.nonPremium],
                          ['Offers OK', notificationStats.preferences.offers],
                          ['Product updates OK', notificationStats.preferences.productUpdates],
                          ['Transit alerts OK', notificationStats.preferences.transitAlerts],
                          ['Daily horoscope OK', notificationStats.preferences.dailyHoroscope],
                          ['Muhurat OK', notificationStats.preferences.muhurat]
                        ].map(([label, value]) => (
                          <Grid key={label} size={{ xs: 6, md: 3 }}>
                            <Paper variant="outlined" sx={{ p: 1.5 }}>
                              <Typography variant="caption" color="text.secondary">{label}</Typography>
                              <Typography variant="h6">{value}</Typography>
                            </Paper>
                          </Grid>
                        ))}
                      </Grid>
                    ) : (
                      <Alert severity="info">No token stats loaded yet.</Alert>
                    )}
                  </CardContent>
                </Card>

                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Suggested Safe Campaigns
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      For anonymous users, use broad onboarding or offer copy. Avoid personal prediction wording unless the user is logged in and has chart data.
                    </Typography>
                    <Stack spacing={1.5}>
                      {[
                        'Complete your birth details to unlock your free kundli.',
                        'Your Kundlistar Studio Pro yearly plan is available at ₹999/year.',
                        'Explore BNN, KP, Jaimini and Astro-Vastu in Kundlistar.',
                        'Create your first chart and get personalized insights.'
                      ].map((copy) => (
                        <Paper key={copy} variant="outlined" sx={{ p: 1.5 }}>
                          <Typography variant="body2">{copy}</Typography>
                        </Paper>
                      ))}
                    </Stack>
                  </CardContent>
                </Card>
                </Stack>
              </Grid>
            </Grid>
          )}

          {tab === 3 && (
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 6 }}>
                <Card>
                  <CardContent>
                    <Stack spacing={2}>
                      <Box>
                        <Typography variant="h6" gutterBottom>
                          Send Custom Email
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Send one individually addressed email per recipient immediately through Studio SMTP. No email worker is required.
                        </Typography>
                      </Box>

                      <TextField
                        select
                        fullWidth
                        label="Target users"
                        value={customEmailForm.targetType}
                        onChange={(event) => setCustomEmailForm({ ...customEmailForm, targetType: event.target.value as EmailTargetType })}
                      >
                        <MenuItem value="manual_recipients">Manual email list</MenuItem>
                        <MenuItem value="registered">All registered users</MenuItem>
                        <MenuItem value="premium">Pro users</MenuItem>
                        <MenuItem value="non_premium">Registered non-Pro users</MenuItem>
                        <MenuItem value="new_premium_48h">New Pro users - last 48 hours</MenuItem>
                        <MenuItem value="new_non_premium_48h">New non-Pro users - last 48 hours</MenuItem>
                        <MenuItem value="selected_users">Selected users from Users tab</MenuItem>
                      </TextField>

                      {customEmailForm.targetType === 'manual_recipients' && (
                        <TextField
                          fullWidth
                          multiline
                          minRows={4}
                          label="Recipients"
                          placeholder="user1@example.com, user2@example.com"
                          value={customEmailForm.recipients}
                          onChange={(event) => setCustomEmailForm({ ...customEmailForm, recipients: event.target.value })}
                          helperText="Paste one email or multiple emails separated by commas, semicolons, or new lines."
                        />
                      )}

                      {customEmailForm.targetType === 'selected_users' && (
                        <Alert severity={selectedUserIds.length > 0 ? 'info' : 'warning'}>
                          {selectedUserIds.length} selected user{selectedUserIds.length === 1 ? '' : 's'} will receive this email.
                        </Alert>
                      )}

                      <TextField
                        fullWidth
                        label="Subject"
                        value={customEmailForm.subject}
                        onChange={(event) => setCustomEmailForm({ ...customEmailForm, subject: event.target.value })}
                      />

                      <TextField
                        fullWidth
                        multiline
                        minRows={8}
                        label="Email body"
                        value={customEmailForm.body}
                        onChange={(event) => setCustomEmailForm({ ...customEmailForm, body: event.target.value })}
                        helperText="Plain text is safest. Line breaks are preserved in the branded email body."
                      />

                      <Button
                        variant="contained"
                        startIcon={customEmailSending ? <CircularProgress size={18} color="inherit" /> : <Send />}
                        disabled={customEmailSending}
                        onClick={sendCustomEmailCampaign}
                      >
                        {customEmailSending ? 'Sending...' : 'Send Custom Email Now'}
                      </Button>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Email Targeting Notes
                    </Typography>
                    <Stack spacing={1.5}>
                      <Alert severity="info">
                        Manual email list sends to exactly the addresses pasted here. Audience segments only send to registered users with valid email addresses.
                      </Alert>
                      <Paper variant="outlined" sx={{ p: 1.5 }}>
                        <Typography variant="subtitle2">Manual recipients</Typography>
                        <Typography variant="body2" color="text.secondary">
                          Use commas, semicolons, or line breaks. Duplicate email addresses are removed before sending.
                        </Typography>
                      </Paper>
                      <Paper variant="outlined" sx={{ p: 1.5 }}>
                        <Typography variant="subtitle2">Selected users workflow</Typography>
                        <Typography variant="body2" color="text.secondary">
                          Go to the Users tab, select rows, then return here and choose “Selected users from Users tab”.
                        </Typography>
                      </Paper>
                      <Paper variant="outlined" sx={{ p: 1.5 }}>
                        <Typography variant="subtitle2">Delivery behavior</Typography>
                        <Typography variant="body2" color="text.secondary">
                          The backend sends a separate email trigger for each user, so recipients never see each other.
                        </Typography>
                      </Paper>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}

          {tab === 4 && (
            <Stack spacing={2}>
              <Card>
                <CardContent>
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }}>
                    <Box>
                      <Typography variant="h6" gutterBottom>
                        Email Campaigns
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Review campaigns sent immediately through Studio SMTP and their sent or failed counts. No email worker is required.
                      </Typography>
                    </Box>
                    <Button
                      variant="outlined"
                      startIcon={emailCampaignsLoading ? <CircularProgress size={16} /> : <Refresh />}
                      onClick={loadEmailCampaigns}
                      disabled={emailCampaignsLoading}
                    >
                      Refresh
                    </Button>
                  </Stack>
                </CardContent>
              </Card>

              <Grid container spacing={2}>
                {[
                  ['Campaigns', emailCampaignTotals?.totalCampaigns ?? 0],
                  ['Total Emails', emailCampaignTotals?.totalEmails ?? 0],
                  ['Sent', emailCampaignTotals?.sent ?? 0],
                  ['Pending', (emailCampaignTotals?.pending ?? 0) + (emailCampaignTotals?.processing ?? 0)],
                  ['Failed', emailCampaignTotals?.failed ?? 0]
                ].map(([label, value]) => (
                  <Grid size={{ xs: 12, sm: 6, md: 2.4 }} key={label}>
                    <Card>
                      <CardContent>
                        <Typography variant="subtitle2">{label}</Typography>
                        <Typography variant="h5">{value}</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>

              <Card>
                <CardContent>
                  {emailCampaignsLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                      <CircularProgress />
                    </Box>
                  ) : emailCampaigns.length === 0 ? (
                    <Alert severity="info">No email campaigns found yet.</Alert>
                  ) : (
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Campaign</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Total</TableCell>
                            <TableCell>Sent</TableCell>
                            <TableCell>Pending</TableCell>
                            <TableCell>Failed</TableCell>
                            <TableCell>Progress</TableCell>
                            <TableCell>Created</TableCell>
                            <TableCell>Completed</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {emailCampaigns.map((campaign) => {
                            const progress = getEmailCampaignProgress(campaign);
                            const pending = campaign.counts.pending + campaign.counts.processing;

                            return (
                              <TableRow key={campaign._id} hover>
                                <TableCell sx={{ maxWidth: 320 }}>
                                  <Typography variant="subtitle2" noWrap title={campaign.title}>
                                    {campaign.title}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary" noWrap title={campaign.subject} component="div">
                                    {campaign.subject}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary" component="div">
                                    ID: {campaign._id}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Chip
                                    label={campaign.status}
                                    color={getEmailCampaignStatusColor(campaign.status)}
                                    size="small"
                                  />
                                </TableCell>
                                <TableCell>{campaign.totalEmails}</TableCell>
                                <TableCell>{campaign.counts.sent}</TableCell>
                                <TableCell>{pending}</TableCell>
                                <TableCell>{campaign.counts.failed}</TableCell>
                                <TableCell sx={{ minWidth: 160 }}>
                                  <Stack spacing={0.5}>
                                    <LinearProgress variant="determinate" value={progress} />
                                    <Typography variant="caption" color="text.secondary">
                                      {progress}%
                                    </Typography>
                                  </Stack>
                                </TableCell>
                                <TableCell>{formatDate(campaign.createdAt, true)}</TableCell>
                                <TableCell>{campaign.completedAt ? formatDate(campaign.completedAt, true) : '-'}</TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </CardContent>
              </Card>
            </Stack>
          )}

          {tab === 6 && (
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 4 }}>
                <Card>
                  <CardContent>
                    <Stack spacing={2}>
                      <Box>
                        <Typography variant="h6" gutterBottom>
                          Send Test Email
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Send demo templates to any inbox to check email copy, spacing, and layout.
                        </Typography>
                      </Box>

                      <TextField
                        fullWidth
                        label="Email to send to"
                        placeholder="name@example.com"
                        value={testEmailTo}
                        onChange={(event) => setTestEmailTo(event.target.value)}
                      />

                      <TextField
                        select
                        fullWidth
                        label="Email template"
                        value={selectedDemoEmailType}
                        onChange={(event) => setSelectedDemoEmailType(event.target.value)}
                      >
                        {demoEmailTypes.map((emailType) => (
                          <MenuItem key={emailType.id} value={emailType.id}>
                            {emailType.label}
                          </MenuItem>
                        ))}
                      </TextField>

                      <Button
                        variant="contained"
                        startIcon={demoEmailSending ? <CircularProgress size={18} color="inherit" /> : <Send />}
                        disabled={demoEmailSending || !selectedDemoEmailType}
                        onClick={() => sendDemoEmailToRecipient()}
                      >
                        {demoEmailSending ? 'Sending...' : 'Send Selected Email'}
                      </Button>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>

              <Grid size={{ xs: 12, md: 8 }}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Available Email Layouts
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      These are the transactional emails currently available for demo sending.
                    </Typography>

                    <TableContainer component={Paper} variant="outlined">
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell>Email</TableCell>
                            <TableCell>Purpose</TableCell>
                            <TableCell>Action</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {demoEmailTypes.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={3} align="center">
                                No demo email templates found.
                              </TableCell>
                            </TableRow>
                          ) : demoEmailTypes.map((emailType) => (
                            <TableRow key={emailType.id} hover>
                              <TableCell>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                  {emailType.label}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {emailType.id}
                                </Typography>
                              </TableCell>
                              <TableCell>{emailType.description}</TableCell>
                              <TableCell>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  startIcon={<Send />}
                                  disabled={demoEmailSending}
                                  onClick={() => {
                                    setSelectedDemoEmailType(emailType.id);
                                    sendDemoEmailToRecipient(emailType.id);
                                  }}
                                >
                                  Send
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}

          {tab === 5 && (
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 4 }}>
                <Card>
                  <CardContent>
                    <Stack spacing={2}>
                      <Box>
                        <Typography variant="h6" gutterBottom>
                          Add City Manually
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Saved cities use the same fields as the world_cities collection. Source and manual flags are added automatically.
                        </Typography>
                      </Box>

                      <TextField
                        fullWidth
                        label="City name"
                        required
                        placeholder="Imphal"
                        value={manualCityForm.name}
                        onChange={(event) => setManualCityForm({ ...manualCityForm, name: event.target.value })}
                      />

                      <Grid container spacing={1.5}>
                        <Grid size={{ xs: 12, md: 4 }}>
                          <TextField
                            fullWidth
                            label="Country code"
                            required
                            placeholder="IN"
                            value={manualCityForm.countryCode}
                            onChange={(event) => setManualCityForm({ ...manualCityForm, countryCode: event.target.value })}
                            inputProps={{ maxLength: 2 }}
                          />
                        </Grid>
                        <Grid size={{ xs: 12, md: 8 }}>
                          <TextField
                            fullWidth
                            label="Country name"
                            required
                            placeholder="India"
                            value={manualCityForm.countryName}
                            onChange={(event) => setManualCityForm({ ...manualCityForm, countryName: event.target.value })}
                          />
                        </Grid>
                      </Grid>

                      <Grid container spacing={1.5}>
                        <Grid size={{ xs: 12, md: 8 }}>
                          <TextField
                            fullWidth
                            label="State / province"
                            placeholder="Manipur"
                            value={manualCityForm.stateName}
                            onChange={(event) => setManualCityForm({ ...manualCityForm, stateName: event.target.value })}
                          />
                        </Grid>
                        <Grid size={{ xs: 12, md: 4 }}>
                          <TextField
                            fullWidth
                            label="State code"
                            placeholder="MN"
                            value={manualCityForm.stateCode}
                            onChange={(event) => setManualCityForm({ ...manualCityForm, stateCode: event.target.value })}
                          />
                        </Grid>
                      </Grid>

                      <Grid container spacing={1.5}>
                        <Grid size={{ xs: 12, md: 6 }}>
                          <TextField
                            fullWidth
                            type="number"
                            label="Latitude"
                            required
                            placeholder="24.80798333"
                            value={manualCityForm.latitude}
                            onChange={(event) => setManualCityForm({ ...manualCityForm, latitude: event.target.value })}
                          />
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                          <TextField
                            fullWidth
                            type="number"
                            label="Longitude"
                            required
                            placeholder="93.94192500"
                            value={manualCityForm.longitude}
                            onChange={(event) => setManualCityForm({ ...manualCityForm, longitude: event.target.value })}
                          />
                        </Grid>
                      </Grid>

                      <TextField
                        fullWidth
                        label="Timezone"
                        required
                        placeholder="Asia/Kolkata"
                        value={manualCityForm.timezone}
                        onChange={(event) => setManualCityForm({ ...manualCityForm, timezone: event.target.value })}
                      />

                      <Grid container spacing={1.5}>
                        <Grid size={{ xs: 12, md: 6 }}>
                          <TextField
                            fullWidth
                            type="number"
                            label="Population"
                            placeholder="0"
                            value={manualCityForm.population}
                            onChange={(event) => setManualCityForm({ ...manualCityForm, population: event.target.value })}
                          />
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                          <TextField
                            fullWidth
                            label="Native name"
                            placeholder="Local/native city name"
                            value={manualCityForm.native}
                            onChange={(event) => setManualCityForm({ ...manualCityForm, native: event.target.value })}
                          />
                        </Grid>
                      </Grid>

                      <TextField
                        fullWidth
                        label="Hindi translation"
                        placeholder="इम्फाल"
                        value={manualCityForm.translationHi}
                        onChange={(event) => setManualCityForm({ ...manualCityForm, translationHi: event.target.value })}
                        helperText="Saved as translations.hi for Hindi city search."
                      />

                      <Alert severity="info">
                        Example: Imphal, MN, Manipur, IN, India, 24.80798333, 93.94192500, Asia/Kolkata.
                      </Alert>

                      <Stack direction="row" spacing={1}>
                        <Button
                          variant="contained"
                          startIcon={manualCitySaving ? <CircularProgress size={18} color="inherit" /> : <LocationCity />}
                          disabled={manualCitySaving}
                          onClick={createManualCity}
                        >
                          {manualCitySaving ? 'Saving...' : 'Add City'}
                        </Button>
                        <Button
                          variant="outlined"
                          disabled={manualCitySaving}
                          onClick={() => setManualCityForm(getDefaultManualCityForm())}
                        >
                          Reset
                        </Button>
                      </Stack>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>

              <Grid size={{ xs: 12, md: 8 }}>
                <Card>
                  <CardContent>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                      <Box>
                        <Typography variant="h6" gutterBottom>
                          Manually Added Cities
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          These rows are stored in the city database with source admin_manual.
                        </Typography>
                      </Box>
                      <Button
                        variant="outlined"
                        startIcon={manualCitiesLoading ? <CircularProgress size={16} /> : <Refresh />}
                        disabled={manualCitiesLoading}
                        onClick={loadManualCities}
                      >
                        Refresh
                      </Button>
                    </Stack>

                    <TableContainer component={Paper} variant="outlined">
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell>City</TableCell>
                            <TableCell>Country Code</TableCell>
                            <TableCell>Coordinates</TableCell>
                            <TableCell>Timezone</TableCell>
                            <TableCell>Population</TableCell>
                            <TableCell>Created</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {manualCities.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={6} align="center">
                                {manualCitiesLoading ? 'Loading manual cities...' : 'No manual cities added yet.'}
                              </TableCell>
                            </TableRow>
                          ) : manualCities.map((city) => (
                            <TableRow key={city.id} hover>
                              <TableCell>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                  {city.name}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {city.displayName}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Chip label={city.country.code} size="small" />
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                  {city.country.name}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                {city.coordinates.latitude}, {city.coordinates.longitude}
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2">{city.timezone?.name || '-'}</Typography>
                                <Typography variant="caption" color="text.secondary">
                                  UTC offset: {city.timezone?.offset ?? '-'} min
                                </Typography>
                              </TableCell>
                              <TableCell>{city.population ?? 0}</TableCell>
                              <TableCell>{city.createdAt ? formatDate(city.createdAt, true) : '-'}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}

          <Dialog
            open={createUserOpen}
            onClose={() => setCreateUserOpen(false)}
            maxWidth="md"
            fullWidth
          >
            <DialogTitle>Create User</DialogTitle>
            <DialogContent dividers>
              <Grid container spacing={2} sx={{ pt: 1 }}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    label="Name"
                    value={newUser.name}
                    onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    label="Email"
                    type="email"
                    value={newUser.email}
                    onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    label="Password"
                    type="password"
                    value={newUser.password}
                    onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 3 }}>
                  <TextField
                    select
                    fullWidth
                    label="Role"
                    value={newUser.role}
                    onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
                  >
                    <MenuItem value="user">User</MenuItem>
                    <MenuItem value="astrologer">Astrologer</MenuItem>
                    <MenuItem value="admin">Admin</MenuItem>
                  </TextField>
                </Grid>
                <Grid size={{ xs: 12, md: 3 }}>
                  <TextField
                    select
                    fullWidth
                    label="Plan"
                    value={newUser.subscriptionType}
                    onChange={(e) => setNewUser({ ...newUser, subscriptionType: e.target.value })}
                  >
                    <MenuItem value="free">Free</MenuItem>
                    <MenuItem value="basic">Basic</MenuItem>
                    <MenuItem value="premium">Pro</MenuItem>
                  </TextField>
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setCreateUserOpen(false)}>Cancel</Button>
              <Button variant="contained" startIcon={<PersonAdd />} onClick={createUser}>
                Create User
              </Button>
            </DialogActions>
          </Dialog>

          <Dialog open={Boolean(selectedUserDetail)} onClose={() => setSelectedUserDetail(null)} maxWidth="md" fullWidth>
            <DialogTitle>User Details</DialogTitle>
            <DialogContent dividers>
              {selectedUserDetail ? (
                <Stack spacing={2} sx={{ pt: 1 }}>
                  <Grid container spacing={2}>
                    {[
                      ['Name', selectedUserDetail.name || '-'],
                      ['Email', selectedUserDetail.email || '-'],
                      ['User Source', selectedUserDetail.accountSource === 'kundlistar' ? 'Kundlistar User' : 'Studio User'],
                      ['Phone', selectedUserDetail.phone || '-'],
                      ['Role', selectedUserDetail.role || '-'],
                      ['Status', selectedUserDetail.isActive ? 'Active' : 'Disabled'],
                      ['Created', formatDate(selectedUserDetail.createdAt, true)],
                      ['Updated', selectedUserDetail.updatedAt ? formatDate(selectedUserDetail.updatedAt, true) : '-'],
                      ['Last Login', selectedUserDetail.lastLogin ? formatDate(selectedUserDetail.lastLogin, true) : '-'],
                      ['Login Count', selectedUserDetail.loginCount ?? selectedUserDetail.stats?.loginCount ?? 0],
                      ['Charts Created', selectedUserDetail.chartsCreated ?? selectedUserDetail.stats?.chartsCreated ?? 0]
                    ].map(([label, value]) => (
                      <Grid key={String(label)} size={{ xs: 12, md: 6 }}>
                        <Paper variant="outlined" sx={{ p: 1.5, height: '100%' }}>
                          <Typography variant="caption" color="text.secondary">{label}</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600, overflowWrap: 'anywhere' }}>{value}</Typography>
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>

                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                      Subscription & Payment Details
                    </Typography>
                    <Grid container spacing={2}>
                      {[
                        ['Plan', selectedUserDetail.subscription?.type === 'premium' ? 'Pro' : selectedUserDetail.subscription?.type || 'free'],
                        ['Subscription Active', selectedUserDetail.subscription?.isActive ? 'Yes' : 'No'],
                        ['Charts Remaining', selectedUserDetail.subscription?.chartsRemaining ?? 'Unlimited'],
                        ['Start Date', selectedUserDetail.subscription?.startDate ? formatDate(selectedUserDetail.subscription.startDate, true) : '-'],
                        ['End Date', selectedUserDetail.subscription?.endDate ? formatDate(selectedUserDetail.subscription.endDate, true) : '-'],
                        ['Payment Source', selectedUserDetail.subscription?.source || '-']
                      ].map(([label, value]) => (
                        <Grid key={String(label)} size={{ xs: 12, md: 6 }}>
                          <Box>
                            <Typography variant="caption" color="text.secondary">{label}</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600, overflowWrap: 'anywhere' }}>{value}</Typography>
                          </Box>
                        </Grid>
                      ))}
                    </Grid>
                  </Paper>

                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                      AI Reports This Month
                    </Typography>
                    <Grid container spacing={2}>
                      {[
                        ['Month', selectedUserDetail.aiReportsCurrentMonth?.monthKey || '-'],
                        ['Reports Generated', selectedUserDetail.aiReportsCurrentMonth?.generated ?? 0],
                        ['Quota Used', selectedUserDetail.aiReportsCurrentMonth?.quotaUsed ?? 0],
                        [
                          'Monthly Limit',
                          selectedUserDetail.aiReportsCurrentMonth?.limit === null
                            ? 'Unlimited'
                            : selectedUserDetail.aiReportsCurrentMonth?.limit ?? '-'
                        ],
                        [
                          'Remaining',
                          selectedUserDetail.aiReportsCurrentMonth?.remaining === null
                            ? 'Unlimited'
                            : selectedUserDetail.aiReportsCurrentMonth?.remaining ?? '-'
                        ],
                        ['Base Pro Limit', selectedUserDetail.aiReportsCurrentMonth?.basePremiumLimit ?? '-'],
                        ['AI Add-on Active', selectedUserDetail.aiReportsCurrentMonth?.addonActive ? 'Yes' : 'No'],
                        ['AI Add-on Plan', selectedUserDetail.aiReportsCurrentMonth?.addonPlan || '-']
                      ].map(([label, value]) => (
                        <Grid key={String(label)} size={{ xs: 12, md: 6 }}>
                          <Box>
                            <Typography variant="caption" color="text.secondary">{label}</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600, overflowWrap: 'anywhere' }}>{value}</Typography>
                          </Box>
                        </Grid>
                      ))}
                    </Grid>
                    {(selectedUserDetail.aiReportsCurrentMonth?.quotaUsed ?? 0) !== (selectedUserDetail.aiReportsCurrentMonth?.generated ?? 0) ? (
                      <Alert severity="warning" sx={{ mt: 2 }}>
                        Quota used and generated reports differ. This can happen if a quota was charged and generation failed, or if older data was recorded before the current quota fix.
                      </Alert>
                    ) : null}
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                        Generated AI Report IDs
                      </Typography>
                      {selectedUserDetail.aiReportsCurrentMonth?.reports?.length ? (
                        <Stack spacing={1}>
                          {selectedUserDetail.aiReportsCurrentMonth.reports.map((report) => (
                            <Paper key={report.id || report._id} variant="outlined" sx={{ p: 1.25 }}>
                              <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600, overflowWrap: 'anywhere' }}>
                                {report.id || report._id}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', overflowWrap: 'anywhere' }}>
                                Chart: {report.chartName || '-'} ({report.chartId || '-'})
                              </Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                Year: {report.reportYear || '-'} | Language: {report.language || '-'} | Generated: {report.generatedAt ? formatDate(report.generatedAt, true) : '-'}
                              </Typography>
                            </Paper>
                          ))}
                        </Stack>
                      ) : (
                        <Alert severity="info">No AI report IDs found for this user in the current month.</Alert>
                      )}
                    </Box>
                  </Paper>

                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                      Latest Payment Record
                    </Typography>
                    {selectedUserDetail.latestPayment ? (
                      <Grid container spacing={2}>
                        {[
                          ['Amount', `${selectedUserDetail.latestPayment.currency || 'INR'} ${selectedUserDetail.latestPayment.amount ?? '-'}`],
                          ['Status', selectedUserDetail.latestPayment.status || '-'],
                          ['Payment Method', selectedUserDetail.latestPayment.paymentMethod || '-'],
                          ['Plan', selectedUserDetail.latestPayment.subscriptionType || '-'],
                          ['Customer Name', selectedUserDetail.latestPayment.customerName || '-'],
                          ['Payment Email', selectedUserDetail.latestPayment.email || '-'],
                          ['Payment Phone', selectedUserDetail.latestPayment.phone || '-'],
                          ['Receipt', selectedUserDetail.latestPayment.receipt || '-'],
                          ['Payment Completed', selectedUserDetail.latestPayment.paymentCompletedAt ? formatDate(selectedUserDetail.latestPayment.paymentCompletedAt, true) : '-'],
                          ['Subscription Start', selectedUserDetail.latestPayment.subscriptionStartDate ? formatDate(selectedUserDetail.latestPayment.subscriptionStartDate, true) : '-'],
                          ['Subscription End', selectedUserDetail.latestPayment.subscriptionEndDate ? formatDate(selectedUserDetail.latestPayment.subscriptionEndDate, true) : '-'],
                          ['Admin Notes', selectedUserDetail.latestPayment.notes?.notes || '-']
                        ].map(([label, value]) => (
                          <Grid key={String(label)} size={{ xs: 12, md: 6 }}>
                            <Box>
                              <Typography variant="caption" color="text.secondary">{label}</Typography>
                              <Typography variant="body2" sx={{ fontWeight: 600, overflowWrap: 'anywhere' }}>{value}</Typography>
                            </Box>
                          </Grid>
                        ))}
                      </Grid>
                    ) : (
                      <Alert severity="info">No payment record found for this user.</Alert>
                    )}
                  </Paper>

                  {selectedUserDetail.subscription?.aiReportAddon ? (
                    <Paper variant="outlined" sx={{ p: 2 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                        AI Report Add-on
                      </Typography>
                      <Grid container spacing={2}>
                        {[
                          ['Plan', selectedUserDetail.subscription.aiReportAddon.plan || '-'],
                          ['Monthly Limit', selectedUserDetail.subscription.aiReportAddon.monthlyLimit ?? '-'],
                          ['Active', selectedUserDetail.subscription.aiReportAddon.isActive ? 'Yes' : 'No'],
                          ['Start Date', selectedUserDetail.subscription.aiReportAddon.startDate ? formatDate(selectedUserDetail.subscription.aiReportAddon.startDate, true) : '-'],
                          ['End Date', selectedUserDetail.subscription.aiReportAddon.endDate ? formatDate(selectedUserDetail.subscription.aiReportAddon.endDate, true) : '-'],
                          ['Payment Source', selectedUserDetail.subscription.source || '-']
                        ].map(([label, value]) => (
                          <Grid key={String(label)} size={{ xs: 12, md: 6 }}>
                            <Box>
                              <Typography variant="caption" color="text.secondary">{label}</Typography>
                              <Typography variant="body2" sx={{ fontWeight: 600, overflowWrap: 'anywhere' }}>{value}</Typography>
                            </Box>
                          </Grid>
                        ))}
                      </Grid>
                    </Paper>
                  ) : null}
                </Stack>
              ) : null}
            </DialogContent>
            <DialogActions>
              {selectedUserDetail ? (
                <>
                  <Button
                    variant="outlined"
                    startIcon={<AutoAwesome />}
                    onClick={() => {
                      openPremiumEditDialog(selectedUserDetail);
                      setSelectedUserDetail(null);
                    }}
                  >
                    Edit Pro
                  </Button>
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<Delete />}
                    disabled={String(selectedUserDetail._id) === currentAdminUserId}
                    onClick={() => deleteUser(selectedUserDetail)}
                  >
                    Delete User
                  </Button>
                </>
              ) : null}
              <Button onClick={() => setSelectedUserDetail(null)}>Close</Button>
            </DialogActions>
          </Dialog>

          <Dialog
            open={premiumEditOpen}
            onClose={() => {
              if (!premiumEditSaving) {
                setPremiumEditOpen(false);
                setPremiumEditError(null);
              }
            }}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>Set / Edit Pro</DialogTitle>
            <DialogContent dividers>
              <Stack spacing={2} sx={{ pt: 1 }}>
                {premiumEditError && (
                  <Alert severity="error" onClose={() => setPremiumEditError(null)}>
                    {premiumEditError}
                  </Alert>
                )}
                <TextField
                  fullWidth
                  label="User"
                  value={premiumEdit.userName}
                  InputProps={{ readOnly: true }}
                />
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      select
                      fullWidth
                      label="Plan"
                      value={premiumEdit.subscriptionType}
                      onChange={(e) => setPremiumEdit({
                        ...premiumEdit,
                        subscriptionType: e.target.value as PremiumEditForm['subscriptionType']
                      })}
                    >
                      <MenuItem value="premium">Pro</MenuItem>
                      <MenuItem value="basic">Basic</MenuItem>
                      <MenuItem value="free">Free</MenuItem>
                    </TextField>
                  </Grid>
                  {premiumEdit.subscriptionType !== 'free' && (
                    <>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <TextField
                          select
                          fullWidth
                          label="Duration"
                          value={premiumEdit.durationPreset}
                          onChange={(e) => {
                            const durationPreset = e.target.value as PremiumDurationPreset;
                            setPremiumEdit({
                              ...premiumEdit,
                              durationPreset,
                              endDate: durationPreset === 'custom_dates'
                                ? premiumEdit.endDate
                                : getPremiumEndDateForPreset(premiumEdit.startDate, durationPreset)
                            });
                          }}
                        >
                          <MenuItem value="monthly">Monthly</MenuItem>
                          <MenuItem value="yearly">Yearly</MenuItem>
                          <MenuItem value="two_days">2 days</MenuItem>
                          <MenuItem value="custom_days">Custom days</MenuItem>
                          <MenuItem value="custom_dates">Custom dates</MenuItem>
                        </TextField>
                      </Grid>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <TextField
                          fullWidth
                          type="date"
                          label="Start Date"
                          required
                          value={premiumEdit.startDate}
                          InputLabelProps={{ shrink: true }}
                          onChange={(e) => {
                            const startDate = e.target.value;
                            setPremiumEdit({
                              ...premiumEdit,
                              startDate,
                              endDate: premiumEdit.durationPreset === 'custom_dates'
                                ? premiumEdit.endDate
                                : getPremiumEndDateForPreset(startDate, premiumEdit.durationPreset)
                            });
                          }}
                        />
                      </Grid>
                      {premiumEdit.durationPreset === 'custom_days' && (
                        <Grid size={{ xs: 12, md: 6 }}>
                          <TextField
                            fullWidth
                            type="number"
                            label="Custom Days"
                            required
                            value={premiumEdit.customDays}
                            onChange={(e) => {
                              const customDays = e.target.value;
                              setPremiumEdit({
                                ...premiumEdit,
                                customDays,
                                endDate: getPremiumEndDateForPreset(premiumEdit.startDate, 'custom_days', customDays)
                              });
                            }}
                          />
                        </Grid>
                      )}
                      <Grid size={{ xs: 12, md: 6 }}>
                        <TextField
                          fullWidth
                          type="date"
                          label="End Date"
                          required={premiumEdit.durationPreset === 'custom_dates'}
                          value={premiumEdit.endDate}
                          InputLabelProps={{ shrink: true }}
                          InputProps={{
                            readOnly: premiumEdit.durationPreset !== 'custom_dates'
                          }}
                          onChange={(e) => setPremiumEdit({ ...premiumEdit, endDate: e.target.value })}
                        />
                      </Grid>
                    </>
                  )}
                </Grid>
                {premiumEdit.subscriptionType === 'free' && (
                  <Alert severity="info">
                    Saving as Free removes active plan access and clears subscription start/end dates.
                  </Alert>
                )}
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => {
                  setPremiumEditOpen(false);
                  setPremiumEditError(null);
                }}
                disabled={premiumEditSaving}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                startIcon={premiumEditSaving ? <CircularProgress size={16} color="inherit" /> : <AutoAwesome />}
                onClick={savePremiumEdit}
                disabled={premiumEditSaving}
              >
                {premiumEditSaving ? 'Saving...' : 'Save Access'}
              </Button>
            </DialogActions>
          </Dialog>

          <Dialog
            open={manualPaymentOpen}
            onClose={() => {
              if (!manualPaymentSaving) {
                setManualPaymentOpen(false);
                setManualPaymentError(null);
              }
            }}
            maxWidth="md"
            fullWidth
          >
            <DialogTitle>Add Manual Payment</DialogTitle>
            <DialogContent dividers>
              <Stack spacing={2} sx={{ pt: 1 }}>
                <Alert severity="warning">
                  This creates a captured payment record and updates the selected user's subscription or AI report add-on access. Payment details are mandatory.
                </Alert>
                {manualPaymentError && (
                  <Alert severity="error" onClose={() => setManualPaymentError(null)}>
                    {manualPaymentError}
                  </Alert>
                )}
                <TextField
                  fullWidth
                  label="User"
                  value={manualPayment.userName}
                  InputProps={{ readOnly: true }}
                />
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <TextField
                      select
                      fullWidth
                      label="Plan"
                      value={manualPayment.subscriptionType}
                      onChange={(e) => setManualPayment({ ...manualPayment, subscriptionType: e.target.value })}
                    >
                      <MenuItem value="premium">Pro</MenuItem>
                      <MenuItem value="basic">Basic</MenuItem>
                      <MenuItem value="ai_report_addon_20">AI Add-on 20 reports/month</MenuItem>
                      <MenuItem value="ai_report_addon_40">AI Add-on 40 reports/month</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <TextField
                      fullWidth
                      label="Amount"
                      type="number"
                      required
                      value={manualPayment.amount}
                      onChange={(e) => setManualPayment({ ...manualPayment, amount: e.target.value })}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <TextField
                      select
                      fullWidth
                      label="Payment Method"
                      required
                      value={manualPayment.paymentMethod}
                      onChange={(e) => setManualPayment({ ...manualPayment, paymentMethod: e.target.value })}
                    >
                      <MenuItem value="upi">UPI</MenuItem>
                      <MenuItem value="card">Card</MenuItem>
                      <MenuItem value="netbanking">Netbanking</MenuItem>
                      <MenuItem value="wallet">Wallet</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      fullWidth
                      type="date"
                      label="Start Date"
                      required
                      value={manualPayment.startDate}
                      InputLabelProps={{ shrink: true }}
                      onChange={(e) => setManualPayment({ ...manualPayment, startDate: e.target.value })}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      fullWidth
                      type="date"
                      label="End Date"
                      required
                      value={manualPayment.endDate}
                      InputLabelProps={{ shrink: true }}
                      onChange={(e) => setManualPayment({ ...manualPayment, endDate: e.target.value })}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      fullWidth
                      label="Receipt"
                      required
                      value={manualPayment.receipt}
                      onChange={(e) => setManualPayment({ ...manualPayment, receipt: e.target.value })}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      fullWidth
                      label="Phone"
                      required
                      value={manualPayment.phone}
                      onChange={(e) => setManualPayment({ ...manualPayment, phone: e.target.value })}
                    />
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <TextField
                      fullWidth
                      multiline
                      minRows={3}
                      label="Admin Notes"
                      required
                      value={manualPayment.notes}
                      onChange={(e) => setManualPayment({ ...manualPayment, notes: e.target.value })}
                    />
                  </Grid>
                </Grid>
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => {
                  setManualPaymentOpen(false);
                  setManualPaymentError(null);
                }}
                disabled={manualPaymentSaving}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                startIcon={manualPaymentSaving ? <CircularProgress size={16} color="inherit" /> : <ReceiptLong />}
                onClick={saveManualPayment}
                disabled={manualPaymentSaving}
              >
                {manualPaymentSaving ? 'Saving...' : 'Save Payment'}
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}
    </Container>
  );
};

export default AdminPanel;
